// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, FlatList, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, TextInput, View } from 'react-native';
import * as Location from 'expo-location';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import PlayerMembershipEditor, {
  type AvailableMember,
  type DraftPlayerMembershipRow,
  type ExistingPlayerMembershipRow,
  type PlayerMembershipRow,
} from '@/components/PlayerMembershipEditor';
import { t } from '@/i18n';
import { useUser } from '@/app/user-context';
import {
  FriendListSnapshotEvent,
  getSharedBffWebSocket,
  PlayersErrorEvent,
  PlayerTemporaryCreatedEvent,
  GolfCourseChangedEvent,
  GolfCourseUpsertRejectedEvent,
  RoundCreatedEvent,
  RoundInitiateRejectedEvent,
  RoundUpdatedEvent,
  TourCreatedEvent,
  TourMemberAddedEvent,
  TourMembersSnapshotEvent,
} from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';
import { setCurrentRoundId } from '@/services/currentRound';
import { formatLocalDate, localDateToDate, todayLocalDate } from '@/services/localDate';
import { useRoundState } from '@/stores/roundState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildRoundUpdateMessage } from '@/services/roundUpdateMessage';
import type {
  AvailablePlayer,
  Course,
  CourseTee,
  Player,
  PlayerScpPrefill,
  RecentCourse,
  TourRoundSummary,
} from '@/features/round-init/types';
import { formatLastPlayed, useRecentCourses } from '@/hooks/useRecentCourses';
import { fillEmptyScpWithCourseHandicap, parseHandicapIndex } from '@/features/round-init/handicap';
import appColors from '@/theme/colors';
import {
  appendPlayerRowIfMissing,
  buildEmptyScpIds,
  buildPlayerOrder,
  deriveInitialTourId,
  normalizeDebugParam,
  upsertAvailablePlayer,
} from '@/features/round-init/utils';
import { resolveTourInviteLink } from '@/services/tourInviteLink';

const MAX_PLAYERS = 8;

const normalizeRequestId = (value?: string | null) => (value ?? '').trim().toLowerCase();

const dedupePlayersById = (input: Player[]) => {
  const seen = new Set<string>();
  const deduped: Player[] = [];
  input.forEach((player) => {
    const playerId = player.id.trim();
    if (!playerId || seen.has(playerId)) {
      return;
    }
    seen.add(playerId);
    deduped.push({
      ...player,
      id: playerId,
    });
  });
  return deduped;
};

// Standard neutral defaults used when the user adds a tee by name only. Slope 113 is the
// WHS neutral slope; course rating defaults to the course par. They keep a name-only tee
// valid for the backend (which drops tees with rating/slope/par <= 0) without forcing the
// user to know the real values.
const NEUTRAL_SLOPE_RATING = 113;

type GeoCourseDetailResponse = {
  name?: string;
  Name?: string;
  address?: string;
  Address?: string;
  visibility?: string;
  Visibility?: string;
  holes?: Array<Record<string, unknown>>;
  Holes?: Array<Record<string, unknown>>;
  tees?: Array<Record<string, unknown>>;
  Tees?: Array<Record<string, unknown>>;
};

const num = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapCourseDetailHoles = (payload: GeoCourseDetailResponse) =>
  (payload.holes ?? payload.Holes ?? [])
    .map((hole) => ({
      number: num(hole.number ?? hole.Number),
      par: num(hole.par ?? hole.Par),
      strokeIndex: num(hole.strokeIndex ?? hole.StrokeIndex),
    }))
    .filter((hole) => hole.number > 0);

const mapCourseDetailTees = (payload: GeoCourseDetailResponse): CourseTee[] =>
  (payload.tees ?? payload.Tees ?? [])
    .map((tee) => ({
      id: String(tee.id ?? tee.Id ?? '').trim(),
      name: String(tee.name ?? tee.Name ?? '').trim(),
      sex: String(tee.sex ?? tee.Sex ?? '').trim(),
      courseRating: num(tee.courseRating ?? tee.CourseRating),
      slopeRating: num(tee.slopeRating ?? tee.SlopeRating),
      par: num(tee.par ?? tee.Par),
    }))
    .filter((tee) => tee.id.length > 0 && tee.name.length > 0);

type PendingRoundInitiatePayload = {
  type: 'round.initiate';
  requestId: string;
  tourId: string;
  name: string;
  description?: string;
  holesCount: number;
  playDate: string;
  players: Array<{ id: string; scp: string }>;
  course: Course;
  gameDefinitionIds: string[];
};

export default function PlayersScreen() {
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { debug: debugParam, tourId: tourIdParam, singleRound: singleRoundParam } = useLocalSearchParams<{
    debug?: string;
    tourId?: string;
    singleRound?: string;
  }>();
  const debug = useMemo(() => normalizeDebugParam(debugParam), [debugParam]);
  const isSingleRoundMode = useMemo(
    () =>
      singleRoundParam === '1'
      || singleRoundParam === 'true'
      || (Array.isArray(singleRoundParam) && singleRoundParam.some((value) => value === '1' || value === 'true')),
    [singleRoundParam]
  );
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'players-screen',
      }),
    [apiBaseUrl, user?.token]
  );
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [courseTees, setCourseTees] = useState<CourseTee[]>([]);
  const [selectedTeeId, setSelectedTeeId] = useState<string | null>(null);
  const [addTeeOpen, setAddTeeOpen] = useState(false);
  const [addTeeName, setAddTeeName] = useState('');
  const [addTeeCourseRating, setAddTeeCourseRating] = useState('');
  const [addTeeSlopeRating, setAddTeeSlopeRating] = useState('');
  const [addTeeSaving, setAddTeeSaving] = useState(false);
  const [addTeeError, setAddTeeError] = useState<string | null>(null);
  // Official WHS handicap index per player id, used to auto-fill an empty playing
  // handicap (scp) from the selected tee's slope/rating/par.
  const [handicapIndexByPlayerId, setHandicapIndexByPlayerId] = useState<Record<string, number>>({});
  const [courseError, setCourseError] = useState<string | null>(null);
  // Hold off surfacing the course-load error for 5s so it doesn't flash on first load (e.g. while
  // geolocation is still resolving); cleared as soon as courses load or a retry succeeds.
  const [courseErrorVisible, setCourseErrorVisible] = useState(false);
  const [coursePickerOpen, setCoursePickerOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [useNearestCourse, setUseNearestCourse] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [savingPlayers, setSavingPlayers] = useState(false);
  const [pendingTemporaryPlayers, setPendingTemporaryPlayers] = useState(0);
  const pendingTourMembersSnapshotRequestIdRef = useRef<string | null>(null);
  const pendingTemporaryCreateByRequestIdRef = useRef<Map<string, { rowId: string; scp: string; name: string }>>(new Map());
  const pendingTemporaryCreateTimeoutByRequestIdRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingMemberAddByRequestIdRef = useRef<Map<string, { playerId: string; name: string; draftRowId?: string; scp?: string }>>(new Map());
  const pendingMemberAddTimeoutByRequestIdRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const roundInitiateRequestIdRef = useRef<string | null>(null);
  const pendingHiddenTourRequestIdRef = useRef<string | null>(null);
  const pendingShareAfterHiddenTourRef = useRef(false);
  const pendingRoundPayloadAfterHiddenTourRef = useRef<PendingRoundInitiatePayload | null>(null);
  // Player ids confirmed as members of the (hidden) tour — sourced from tour.members.snapshot and
  // tour.member.added. In single-round mode a round can only be initiated once every selected player
  // is a confirmed member, otherwise tour-service rejects with round.initiate.membershipRequired.
  const confirmedTourMemberIdsRef = useRef<Set<string>>(new Set());
  // A round.initiate payload held back until the players above are all confirmed members.
  const pendingRoundPayloadAfterMembershipRef = useRef<PendingRoundInitiatePayload | null>(null);
  const mergeNextMembersSnapshotRef = useRef(false);
  const roundAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigateOnRoundCreatedRef = useRef(false);
  const lastAutoRoundInitiateKeyRef = useRef<string | null>(null);
  const lastRoundSettingsKeyRef = useRef<string | null>(null);
  const lastSyncedShcpSignatureRef = useRef<string | null>(null);
  const syncedRoundMemberKeysRef = useRef<Set<string>>(new Set());
  const [roundName] = useState('');
  const [roundDescription, setRoundDescription] = useState('');
  const [roundDescriptionOpen, setRoundDescriptionOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const [shareQrVisible, setShareQrVisible] = useState(false);
  const [holesCount, setHolesCount] = useState(18);
  const [roundPlayDate, setRoundPlayDate] = useState(() => todayLocalDate());
  const [playDatePickerOpen, setPlayDatePickerOpen] = useState(false);
  const initialTourId = useMemo(() => deriveInitialTourId(tourIdParam, getCurrentTourId()), [tourIdParam]);
  const [tourId, setTourId] = useState<string | null>(initialTourId);
  const roundId = useRoundState((state) => state.roundId);
  const [managedRoundId, setManagedRoundId] = useState<string | null>(null);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setHole = useRoundState((state) => state.setHole);
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [friendDirectory, setFriendDirectory] = useState<FriendListSnapshotEvent['friends']>([]);
  const friendPlayerIdsRef = useRef<Set<string>>(new Set());
  const [currentUserPlayerId, setCurrentUserPlayerId] = useState<string | null>(null);
  const recentCourses = useRecentCourses(currentUserPlayerId, user?.token);
  const [draftRows, setDraftRows] = useState<DraftPlayerMembershipRow[]>([]);
  const [quickAddPlayerName, setQuickAddPlayerName] = useState('');
  const [quickAddScp, setQuickAddScp] = useState('');
  const [playerRowOpen, setPlayerRowOpen] = useState<string | null>(null);
  const [pendingRoundStartAfterDraftSave, setPendingRoundStartAfterDraftSave] = useState(false);
  const pendingCreatedCourseSelectionRef = useRef(false);
  const pendingCreatedCourseSelectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scpPrefillKeyRef = useRef<string | null>(null);
  const [activeRounds, setActiveRounds] = useState<TourRoundSummary[]>([]);
  const [activeRoundsLoading, setActiveRoundsLoading] = useState(false);
  const [activeRoundsError, setActiveRoundsError] = useState<string | null>(null);
  const [finishedRounds, setFinishedRounds] = useState<TourRoundSummary[]>([]);
  const [finishedRoundsLoading, setFinishedRoundsLoading] = useState(false);
  const [openRoundMenuId, setOpenRoundMenuId] = useState<string | null>(null);
  const availablePlayerIdSet = useMemo(() => new Set(availablePlayers.map((p) => p.id)), [availablePlayers]);
  const emptyScpIds = useMemo(() => buildEmptyScpIds(players, availablePlayerIdSet), [availablePlayerIdSet, players]);
  const emptyScpKey = useMemo(() => emptyScpIds.join(','), [emptyScpIds]);
  const existingRows = useMemo<ExistingPlayerMembershipRow[]>(
    () =>
      players.map((player) => ({
        kind: 'existing',
        rowId: `existing-${player.id}`,
        playerId: player.id,
        name: player.name,
        locked: Boolean(currentUserPlayerId && player.id === currentUserPlayerId),
      })),
    [currentUserPlayerId, players]
  );
  const membershipRows = useMemo<PlayerMembershipRow[]>(() => [...existingRows, ...draftRows], [draftRows, existingRows]);
  const availableMembersForEditor = useMemo<AvailableMember[]>(() => {
    const merged = new Map<string, AvailableMember>();

    (friendDirectory ?? []).forEach((friend) => {
      if (!friend.playerId) return;
      const name = (friend.playerName ?? friend.name ?? '').trim();
      if (!name) return;
      merged.set(friend.playerId, {
        id: friend.playerId,
        name,
        userId: friend.userId ?? null,
        isFriend: true,
      });
    });

    availablePlayers
      .filter((player) => player.isFriend === true)
      .forEach((player) => {
        if (merged.has(player.id)) return;
        merged.set(player.id, {
          id: player.id,
          name: player.name,
          userId: player.userId ?? null,
          isFriend: true,
        });
      });

    return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [availablePlayers, friendDirectory]);
  const canAddPlayerRow = useMemo(() => {
    return players.length < MAX_PLAYERS;
  }, [players.length]);
  const showRoundShare = Boolean(tourId) || isSingleRoundMode;
  const hiddenRoundStartErrors = useMemo(
    () =>
      new Set([
        t('players.roundInitiateSendFailed'),
        t('players.roundInitiateTimeout'),
        t('players.roundInitiateRejected'),
        t('players.hiddenTourInitiateFailed'),
      ]),
    [t]
  );
  const visiblePlayerError = useMemo(() => {
    const message = (playerError ?? '').trim();
    if (!message) {
      return null;
    }
    if (hiddenRoundStartErrors.has(message)) {
      return null;
    }
    if (message.toLowerCase().includes('unable to start')) {
      return null;
    }
    return message;
  }, [hiddenRoundStartErrors, playerError]);
  useEffect(() => {
    if (!courseError) {
      setCourseErrorVisible(false);
      return;
    }
    const timer = setTimeout(() => setCourseErrorVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [courseError]);
  const visibleCourseError = courseErrorVisible ? courseError : null;
  const continueLabel = useMemo(() => {
    const baseLabel = savingPlayers ? t('home.saving') : `${t('players.roundContinue')} →`;
    if (savingPlayers) {
      return baseLabel;
    }
    const validationMessage = (visibleCourseError ?? visiblePlayerError ?? '').trim();
    if (!validationMessage) {
      return baseLabel;
    }
    return `${baseLabel} (${validationMessage})`;
  }, [visibleCourseError, savingPlayers, t, visiblePlayerError]);
  const debugPendingTemporaryCreates = useMemo(
    () =>
      debug
        ? Array.from(pendingTemporaryCreateByRequestIdRef.current.entries()).map(([requestId, pending]) => ({
            requestId,
            ...pending,
          }))
        : [],
    [debug, pendingTemporaryPlayers]
  );

  const syncPendingTemporaryPlayers = useCallback(() => {
    setPendingTemporaryPlayers(
      pendingTemporaryCreateByRequestIdRef.current.size + pendingMemberAddByRequestIdRef.current.size
    );
  }, []);

  const clearPendingTemporaryTracking = useCallback(() => {
    pendingTemporaryCreateByRequestIdRef.current.clear();
    pendingTemporaryCreateTimeoutByRequestIdRef.current.forEach((timeout) => clearTimeout(timeout));
    pendingTemporaryCreateTimeoutByRequestIdRef.current.clear();
    pendingMemberAddByRequestIdRef.current.clear();
    pendingMemberAddTimeoutByRequestIdRef.current.forEach((timeout) => clearTimeout(timeout));
    pendingMemberAddTimeoutByRequestIdRef.current.clear();
    setPendingTemporaryPlayers(0);
  }, []);

  const sendTourMemberAdd = useCallback(
    (playerId: string, playerName: string, draftRowId?: string, scp?: string) => {
      if (!tourId || !user?.token || !playerId) {
        return false;
      }

      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      const requestId = generateId();
      pendingMemberAddByRequestIdRef.current.set(requestId, { playerId, name: playerName, draftRowId, scp });
      syncPendingTemporaryPlayers();

      const timeout = setTimeout(() => {
        if (!pendingMemberAddByRequestIdRef.current.has(requestId)) {
          return;
        }
        pendingMemberAddByRequestIdRef.current.delete(requestId);
        pendingMemberAddTimeoutByRequestIdRef.current.delete(requestId);
        syncPendingTemporaryPlayers();
        setPlayerError(t('players.membersUpdateTimeout'));
        logToBff('players.members.add.timeout', { tourId, requestId, playerId }, 'warn');
      }, 12000);
      pendingMemberAddTimeoutByRequestIdRef.current.set(requestId, timeout);

      const sent = socket.send({
        type: 'tour.member.add',
        tourId,
        requestId,
        playerId,
      });

      if (!sent) {
        logToBff('players.members.add.queued', { tourId, requestId, playerId }, 'warn');
        return true;
      }

      return true;
    },
    [apiBaseUrl, logToBff, syncPendingTemporaryPlayers, tourId, user?.token]
  );

  const sendRoundMemberAdd = useCallback(
    (targetRoundId: string, playerId: string) => {
      if (!targetRoundId || !playerId || !user?.token) {
        return;
      }
      const key = `${targetRoundId}:${playerId}`;
      if (syncedRoundMemberKeysRef.current.has(key)) {
        return;
      }
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      const requestId = generateId();
      const sent = socket.send({
        type: 'round.member.add',
        roundId: targetRoundId,
        requestId,
        playerId,
      });
      syncedRoundMemberKeysRef.current.add(key);
      if (!sent) {
        logToBff('players.round.member.add.queued', { roundId: targetRoundId, requestId, playerId }, 'warn');
      } else {
        logToBff('players.round.member.add.sent', { roundId: targetRoundId, requestId, playerId });
      }
    },
    [apiBaseUrl, logToBff, user?.token]
  );

  const setDraftStatus = useCallback((rowId: string, status: DraftPlayerMembershipRow['status']) => {
    setDraftRows((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, status } : row)));
  }, []);

  const parseDateInput = (value: string) => localDateToDate(value);
  const formatDateInput = (value: Date) => formatLocalDate(value);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (!isFocused) return;
    const raw = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    if (raw) return;
    const stored = getCurrentTourId();
    if (stored && stored !== tourId) {
      setTourId(stored);
    }
  }, [isFocused, tourId, tourIdParam]);

  useEffect(() => {
    if (!isSingleRoundMode || tourId || !isNavigationReady || !user?.token) {
      return;
    }
    if (pendingHiddenTourRequestIdRef.current) {
      return;
    }

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const hiddenTourRequestId = generateId();
    pendingHiddenTourRequestIdRef.current = hiddenTourRequestId;
    const sent = socket.send({
      type: 'tour.initiate',
      requestId: hiddenTourRequestId,
      tourName: t('players.singleRoundHiddenTourName'),
      playerIds: [],
      isHidden: true,
    });
    if (!sent) {
      logToBff('players.hidden.tour.initiate.queued', { requestId: hiddenTourRequestId }, 'warn');
    }
  }, [apiBaseUrl, isNavigationReady, isSingleRoundMode, logToBff, t, tourId, user?.token]);

  useEffect(() => {
    if (managedRoundId) {
      setCurrentRoundId(managedRoundId);
      return;
    }
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [managedRoundId, roundId]);

  useEffect(() => {
    if (!managedRoundId) {
      syncedRoundMemberKeysRef.current.clear();
      return;
    }
    const next = new Set<string>();
    syncedRoundMemberKeysRef.current.forEach((key) => {
      if (key.startsWith(`${managedRoundId}:`)) {
        next.add(key);
      }
    });
    syncedRoundMemberKeysRef.current = next;
  }, [managedRoundId]);

  const fetchCourses = useCallback(
    async () => {
      if (!isNavigationReady || !user?.token) return;
      setLoadingCourses(true);
      setCourseError(null);
      try {
        if (!coords) {
          setCourseError(t('course.error'));
          setLoadingCourses(false);
          return;
        }
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses?lat=${coords.lat}&lon=${coords.lon}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(t('course.error'));
        }
        const data = (await response.json()) as Course[];
        setCourses(data);
        setSelectedCourseId((prev) => {
          if (prev && data.some((course) => course.id === prev)) {
            return prev;
          }
          return null;
        });
        logToBff('players.courses.loaded', { count: data.length, nearestEnabled: useNearestCourse });
      } catch (err) {
        setCourseError(t('course.error'));
        logToBff('players.courses.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setLoadingCourses(false);
      }
    },
    [apiBaseUrl, coords, isNavigationReady, logToBff, useNearestCourse, user?.token]
  );

  const requestTourMembersSnapshot = useCallback(() => {
    if (!tourId || !isNavigationReady) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const requestId = generateId();
    pendingTourMembersSnapshotRequestIdRef.current = requestId;
    socket.send({
      type: 'tour.members.request',
      requestId,
      tourId,
    });
    logToBff('players.members.request', { tourId, requestId });
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  const requestFriendSnapshot = useCallback(() => {
    if (!isNavigationReady || !user?.token) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    socket.sendFriendListRequest(generateId());
  }, [apiBaseUrl, isNavigationReady, user?.token]);

  const sortByFriendPriority = useCallback((players: AvailablePlayer[]) => {
    const friendIds = friendPlayerIdsRef.current;
    return [...players].sort((a, b) => {
      const aFriend = friendIds.has(a.id);
      const bFriend = friendIds.has(b.id);
      if (aFriend !== bFriend) {
        return aFriend ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, []);

  useEffect(() => {
    if (!isNavigationReady) return;
    fetchCourses();
  }, [fetchCourses, isNavigationReady]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ tourId });
  }, [apiBaseUrl, isFocused, isNavigationReady, tourId]);

  useEffect(() => {
    requestTourMembersSnapshot();
  }, [requestTourMembersSnapshot]);

  useEffect(() => {
    if (!isFocused) return;
    requestFriendSnapshot();
  }, [isFocused, requestFriendSnapshot]);

  const fetchActiveRounds = useCallback(async () => {
    if (!tourId || !isNavigationReady) return;
    setActiveRoundsLoading(true);
    setActiveRoundsError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,active,playing`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(`Failed to load rounds (${response.status})`);
      }
      const data = (await response.json()) as TourRoundSummary[];
      setActiveRounds(data);
    } catch (err) {
      setActiveRoundsError(t('players.roundsLoadError'));
      logToBff('players.rounds.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setActiveRoundsLoading(false);
    }
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  const fetchFinishedRounds = useCallback(async () => {
    if (!tourId || !isNavigationReady) return;
    setFinishedRoundsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=finished`, {
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
      });
      if (!response.ok) {
        throw new Error(`Failed to load finished rounds (${response.status})`);
      }
      const data = (await response.json()) as TourRoundSummary[];
      setFinishedRounds(data);
    } catch (err) {
      logToBff('players.rounds.finished.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
    } finally {
      setFinishedRoundsLoading(false);
    }
  }, [apiBaseUrl, isNavigationReady, logToBff, tourId, user?.token]);

  useEffect(() => {
    fetchActiveRounds();
  }, [fetchActiveRounds]);

  useEffect(() => {
    fetchFinishedRounds();
  }, [fetchFinishedRounds]);

  useEffect(() => {
    if (!isFocused || !isNavigationReady || !user?.token) return;
    void fetchActiveRounds();
    void fetchFinishedRounds();
  }, [fetchActiveRounds, fetchFinishedRounds, isFocused, isNavigationReady, user?.token]);

  useEffect(() => {
    if (!isNavigationReady || !user?.token || !user?.id) return;
    let cancelled = false;

    const ensureCurrentUserPlayer = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;

        const payload = (await response.json()) as { id?: string; name?: string; displayName?: string | null; handicap?: unknown; Handicap?: unknown };
        const playerId = payload.id?.trim();
        const playerName = (payload.displayName ?? payload.name ?? '').trim();
        if (!playerId || !playerName || cancelled) {
          return;
        }

        const currentUserHandicap = parseHandicapIndex(payload.handicap ?? payload.Handicap);
        if (currentUserHandicap !== null) {
          setHandicapIndexByPlayerId((prev) => ({ ...prev, [playerId]: currentUserHandicap }));
        }
        setCurrentUserPlayerId(playerId);
        setAvailablePlayers((prev) => {
          const updated = upsertAvailablePlayer(
            prev,
            {
              id: playerId,
              name: playerName,
              userId: user.id,
              isTemporary: false,
              isFriend: friendPlayerIdsRef.current.has(playerId),
            }
          );
          return updated;
        });
        setPlayers((prev) => appendPlayerRowIfMissing(prev, { id: playerId, name: playerName, scp: '' }, MAX_PLAYERS));
      } catch {
        // Ignore and rely on tour.members.snapshot updates.
      }
    };

    void ensureCurrentUserPlayer();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, user?.id, user?.token]);

  useEffect(() => {
    if (!isNavigationReady || !selectedCourseId || emptyScpIds.length === 0) return;
    const key = `${selectedCourseId}:${emptyScpKey}`;
    if (scpPrefillKeyRef.current === key) return;
    scpPrefillKeyRef.current = key;
    const controller = new AbortController();

    const fetchPrefill = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/players/scp/latest?courseId=${encodeURIComponent(selectedCourseId)}&playerIds=${emptyScpKey}`,
          {
            headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          logToBff('players.scp.prefill.failed', { status: response.status });
          return;
        }
        const data = (await response.json()) as PlayerScpPrefill[];
        if (!data.length) return;
        const scpMap = new Map<string, string>();
        data.forEach((entry) => {
          const playerId = entry.playerId ?? entry.PlayerId;
          const scp = entry.scp ?? entry.Scp;
          if (!playerId || !scp) return;
          scpMap.set(playerId, scp);
        });
        if (scpMap.size === 0) return;
        setPlayers((prev) =>
          prev.map((player) => {
            if (player.scp.trim()) return player;
            const scp = scpMap.get(player.id);
            return scp ? { ...player, scp } : player;
          })
        );
        logToBff('players.scp.prefill.applied', { count: scpMap.size, courseId: selectedCourseId });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        logToBff('players.scp.prefill.error', { message: err instanceof Error ? err.message : String(err) }, 'warn');
      }
    };

    fetchPrefill();
    return () => controller.abort();
  }, [apiBaseUrl, emptyScpIds.length, emptyScpKey, isNavigationReady, logToBff, selectedCourseId, user?.token]);

  // Auto-fill an empty playing handicap (scp) from the player's official WHS
  // handicap index and the selected tee's slope/rating/par. Only fills blanks, so
  // it never overrides a value the user typed or one already prefilled from a prior
  // round. Re-runs when the tee, hole count, handicap data, or the set of
  // empty-scp players changes.
  useEffect(() => {
    const tee = courseTees.find((candidate) => candidate.id === selectedTeeId) ?? null;
    setPlayers((prev) => fillEmptyScpWithCourseHandicap(prev, handicapIndexByPlayerId, tee, holesCount));
  }, [courseTees, selectedTeeId, holesCount, handicapIndexByPlayerId, emptyScpKey]);

  useEffect(() => {
    if (!coords || !isNavigationReady) return;
    fetchCourses();
  }, [coords, fetchCourses, isNavigationReady]);

  // Re-fetch the course list whenever this screen regains focus — e.g. after
  // creating a course via "Add course" — so a just-saved course shows up in the
  // picker. Without this the list only loads on mount / GPS change, leaving the
  // new course invisible until a full reload.
  const wasFocusedRef = useRef(true);
  useEffect(() => {
    if (isFocused && !wasFocusedRef.current && isNavigationReady && coords) {
      fetchCourses();
    }
    wasFocusedRef.current = isFocused;
  }, [isFocused, isNavigationReady, coords, fetchCourses]);

  useEffect(() => {
    if (!isNavigationReady || !user?.token || !selectedCourseId) {
      setCourseTees([]);
      setSelectedTeeId(null);
      return;
    }

    let cancelled = false;
    const loadCourseTees = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          if (!cancelled) {
            setCourseTees([]);
            setSelectedTeeId(null);
          }
          return;
        }

        const payload = (await response.json()) as {
          tees?: Array<{
            id?: string;
            name?: string;
            sex?: string;
            courseRating?: number;
            slopeRating?: number;
            par?: number;
          }>;
          Tees?: Array<{
            Id?: string;
            Name?: string;
            Sex?: string;
            CourseRating?: number;
            SlopeRating?: number;
            Par?: number;
          }>;
        };

        const teeList = (payload.tees ?? payload.Tees ?? [])
          .map((tee) => ({
            id: (tee.id ?? (tee as { Id?: string }).Id ?? '').trim(),
            name: (tee.name ?? (tee as { Name?: string }).Name ?? '').trim(),
            sex: (tee.sex ?? (tee as { Sex?: string }).Sex ?? '').trim(),
            courseRating: Number(tee.courseRating ?? (tee as { CourseRating?: number }).CourseRating ?? 0),
            slopeRating: Number(tee.slopeRating ?? (tee as { SlopeRating?: number }).SlopeRating ?? 0),
            par: Number(tee.par ?? (tee as { Par?: number }).Par ?? 0),
          }))
          .filter((tee) => tee.id.length > 0 && tee.name.length > 0);

        if (cancelled) return;
        setCourseTees(teeList);
        setSelectedTeeId((prev) => (prev && teeList.some((tee) => tee.id === prev) ? prev : null));
      } catch {
        if (cancelled) return;
        setCourseTees([]);
        setSelectedTeeId(null);
      }
    };

    void loadCourseTees();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, selectedCourseId, user?.token]);

  useEffect(() => {
    const requestLocation = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setUseNearestCourse(false);
          setCoords({ lat: 57.7089, lon: 11.9746 });
          return;
        }
        setUseNearestCourse(true);
        const position = await Location.getCurrentPositionAsync({});
        setCoords({ lat: position.coords.latitude, lon: position.coords.longitude });
      } catch {
        setUseNearestCourse(false);
        setCoords({ lat: 57.7089, lon: 11.9746 });
      }
    };
    requestLocation().catch(() => {
      setUseNearestCourse(false);
      setCoords({ lat: 57.7089, lon: 11.9746 });
    });
  }, []);

  useEffect(() => {
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      if ((message as { type?: string }).type === 'golfCourse.changed') {
        const payload = message as GolfCourseChangedEvent;
        if (!pendingCreatedCourseSelectionRef.current) {
          return;
        }

        const changedCourseId = (payload.courseId ?? '').trim();
        if (!changedCourseId) {
          return;
        }

        pendingCreatedCourseSelectionRef.current = false;
        if (pendingCreatedCourseSelectionTimeoutRef.current) {
          clearTimeout(pendingCreatedCourseSelectionTimeoutRef.current);
          pendingCreatedCourseSelectionTimeoutRef.current = null;
        }

        const changedCourseName = (payload.name ?? '').trim();
        const applyCourseSelection = (resolvedName: string, resolvedAddress = '') => {
          setCourses((prev) => {
            const idx = prev.findIndex((course) => course.id === changedCourseId);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = {
                ...next[idx],
                name: resolvedName || next[idx].name,
                address: resolvedAddress || next[idx].address,
              };
              return next;
            }
            return [
              ...prev,
              {
                id: changedCourseId,
                name: resolvedName || changedCourseId,
                address: resolvedAddress,
                lat: 0,
                lon: 0,
              },
            ];
          });
          setSelectedCourseId(changedCourseId);
          if (resolvedName) {
            setSelectedCourseName(resolvedName);
            setCourseSearchQuery(resolvedName);
          }
          setCourseError(null);
        };

        if (changedCourseName) {
          applyCourseSelection(changedCourseName);
          return;
        }

        void (async () => {
          try {
            const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${changedCourseId}`, {
              headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
            });
            if (!response.ok) {
              applyCourseSelection('');
              return;
            }
            const data = (await response.json()) as { name?: string; Name?: string; address?: string; Address?: string };
            applyCourseSelection((data.name ?? data.Name ?? '').trim(), (data.address ?? data.Address ?? '').trim());
          } catch {
            applyCourseSelection('');
          }
        })();
        return;
      }
      if ((message as { type?: string }).type === 'tour.members.snapshot') {
        const payload = message as TourMembersSnapshotEvent;
        if (tourId && payload.tourId && payload.tourId !== tourId) {
          return;
        }
        if (
          pendingTourMembersSnapshotRequestIdRef.current &&
          payload.requestId &&
          payload.requestId !== pendingTourMembersSnapshotRequestIdRef.current
        ) {
          return;
        }
        pendingTourMembersSnapshotRequestIdRef.current = null;
        const members = payload.members ?? [];
        members.forEach((member) => {
          if (member.playerId) {
            confirmedTourMemberIdsRef.current.add(member.playerId);
          }
        });
        const existingNameByPlayerId = new Map(players.map((player) => [player.id, player.name]));
        const available = new Map<string, AvailablePlayer>();
        members.forEach((member) => {
          if (!member.playerId) return;
          available.set(member.playerId, {
            id: member.playerId,
            name: member.name.trim() || existingNameByPlayerId.get(member.playerId)?.trim() || member.playerId,
            userId: member.userId ?? null,
            isTemporary: Boolean(member.isTemporary),
            isFriend: friendPlayerIdsRef.current.has(member.playerId),
          });
        });
        const availableList = sortByFriendPriority(Array.from(available.values()));
        const currentPlayer = user?.id ? availableList.find((player) => player.userId === user.id) : undefined;
        setCurrentUserPlayerId((prev) => currentPlayer?.id ?? prev);
        setAvailablePlayers((prev) => {
          let next = prev;
          availableList.forEach((player) => {
            next = upsertAvailablePlayer(next, player);
          });
          return sortByFriendPriority(next);
        });
        setPlayers((prev) => {
          const previousById = new Map(prev.map((player) => [player.id, player]));
          const scpById = new Map(prev.map((player) => [player.id, player.scp]));
          const incomingPlayersRaw = members
            .filter((member) => member.playerId)
            .map((member) => ({
              id: member.playerId,
              name: member.name.trim() || previousById.get(member.playerId)?.name?.trim() || member.playerId,
              scp: scpById.get(member.playerId) ?? '',
            }));
          const incomingPlayers = dedupePlayersById(incomingPlayersRaw).slice(0, MAX_PLAYERS);
          const shouldMerge =
            mergeNextMembersSnapshotRef.current ||
            pendingMemberAddByRequestIdRef.current.size > 0 ||
            pendingTemporaryCreateByRequestIdRef.current.size > 0;
          mergeNextMembersSnapshotRef.current = false;
          if (incomingPlayers.length === 0) {
            if (currentPlayer?.id) {
              const base = [{ id: currentPlayer.id, name: currentPlayer.name, scp: scpById.get(currentPlayer.id) ?? '' }];
              if (shouldMerge) {
                const localExtras = prev.filter((p) => p.id !== currentPlayer.id);
                return dedupePlayersById([...base, ...localExtras]).slice(0, MAX_PLAYERS);
              }
              return base;
            }
            return shouldMerge ? prev : [];
          }
          if (shouldMerge) {
            const incomingIds = new Set(incomingPlayers.map((p) => p.id));
            const localExtras = prev.filter((p) => !incomingIds.has(p.id));
            return buildPlayerOrder(dedupePlayersById([...incomingPlayers, ...localExtras]), currentPlayer, generateId).slice(0, MAX_PLAYERS);
          }
          return buildPlayerOrder(incomingPlayers, currentPlayer, generateId).slice(0, MAX_PLAYERS);
        });
        logToBff('players.members.snapshot', { tourId: payload.tourId, members: members.length });
        return;
      }
      if ((message as { type?: string }).type === 'friend.list.snapshot') {
        const payload = message as FriendListSnapshotEvent;
        setFriendDirectory(payload.friends ?? []);
        const ids = new Set(
          (payload.friends ?? [])
            .map((friend) => friend.playerId)
            .filter((playerId): playerId is string => Boolean(playerId))
        );
        friendPlayerIdsRef.current = ids;
        setAvailablePlayers((prev) => {
          const merged = new Map<string, AvailablePlayer>(
            prev.map((player) => [
              player.id,
              {
                ...player,
                isFriend: ids.has(player.id),
              },
            ])
          );
          (payload.friends ?? []).forEach((friend) => {
            if (!friend.playerId) return;
            const existing = merged.get(friend.playerId);
            merged.set(friend.playerId, {
              id: friend.playerId,
              name: (friend.playerName ?? friend.name ?? '').trim() || existing?.name || friend.playerId,
              userId: friend.userId ?? existing?.userId ?? null,
              isTemporary: existing?.isTemporary ?? (friend.userId == null),
              isFriend: true,
            });
          });
          return sortByFriendPriority(Array.from(merged.values()));
        });
        return;
      }
      if ((message as { type?: string }).type === 'player_error') {
        const payload = message as PlayersErrorEvent;
        const backendError = (payload.error ?? payload.Error ?? '').trim();
        const hasPendingTemporaryCreates = pendingTemporaryCreateByRequestIdRef.current.size > 0;
        if (!hasPendingTemporaryCreates) {
          return;
        }
        const normalizedError = backendError.toLowerCase();
        const isTemporaryCreateRelated =
          normalizedError.includes('temporary')
          || normalizedError.includes('player name is required')
          || normalizedError.includes('player.name.missing');
        if (!isTemporaryCreateRelated) {
          return;
        }
        pendingTemporaryCreateByRequestIdRef.current.clear();
        pendingTemporaryCreateTimeoutByRequestIdRef.current.forEach((timeout) => clearTimeout(timeout));
        pendingTemporaryCreateTimeoutByRequestIdRef.current.clear();
        setDraftRows((prev) => prev.map((row) => ({ ...row, status: 'idle' })));
        syncPendingTemporaryPlayers();
        setPlayerError(backendError || t('players.temporaryCreateFailed'));
        return;
      }
      if (
        (message as { type?: string }).type === 'friend.added'
        || (message as { type?: string }).type === 'friend.removed'
        || (message as { type?: string }).type === 'friend.updated'
      ) {
        requestFriendSnapshot();
        return;
      }
      if (
        (message as { type?: string }).type === 'player.temporary.created'
        || (message as { type?: string }).type === 'players.temporary.created'
      ) {
        const payload = message as PlayerTemporaryCreatedEvent;
        const pendingTemporaryEntries = pendingTemporaryCreateByRequestIdRef.current;
        const payloadRequestId = normalizeRequestId(payload.requestId);
        let resolvedRequestId =
          payloadRequestId && pendingTemporaryEntries.has(payloadRequestId)
            ? payloadRequestId
            : '';
        if (!resolvedRequestId && pendingTemporaryEntries.size === 1) {
          resolvedRequestId = Array.from(pendingTemporaryEntries.keys())[0] ?? '';
        }
        if (!resolvedRequestId && pendingTemporaryEntries.size > 0) {
          resolvedRequestId = Array.from(pendingTemporaryEntries.keys())[0] ?? '';
        }
        if (!resolvedRequestId) {
          const payloadName = (payload.name ?? '').trim().toLowerCase();
          if (payloadName) {
            const matchedByName = Array.from(pendingTemporaryEntries.entries()).find(([, pending]) => {
              const pendingName = (pending.name ?? '').trim().toLowerCase();
              return pendingName.length > 0 && pendingName === payloadName;
            });
            resolvedRequestId = matchedByName?.[0] ?? '';
          }
        }
        if (!resolvedRequestId || !pendingTemporaryEntries.has(resolvedRequestId)) {
          logToBff(
            'players.temporary.create.unmatched',
            {
              payloadRequestId,
              payloadName: (payload.name ?? '').trim(),
              pendingCount: pendingTemporaryEntries.size,
            },
            'warn'
          );
          return;
        }

        const pendingDraft = pendingTemporaryEntries.get(resolvedRequestId);
        const draftRowId = pendingDraft?.rowId;
        const pendingScp = pendingDraft?.scp ?? '';
        pendingTemporaryEntries.delete(resolvedRequestId);
        const pendingTimeout = pendingTemporaryCreateTimeoutByRequestIdRef.current.get(resolvedRequestId);
        if (pendingTimeout) {
          clearTimeout(pendingTimeout);
          pendingTemporaryCreateTimeoutByRequestIdRef.current.delete(resolvedRequestId);
        }
        syncPendingTemporaryPlayers();

        const createdPlayerId = payload.playerId;
        const createdPlayerName = (payload.name?.trim() ?? '') || (pendingDraft?.name?.trim() ?? '');
        if (!createdPlayerId || !createdPlayerName) {
          setPlayerError(t('players.temporaryCreateFailed'));
          if (draftRowId) {
            setDraftStatus(draftRowId, 'idle');
          }
          return;
        }
        logToBff('players.temporary.create.resolved', {
          requestId: resolvedRequestId,
          playerId: createdPlayerId,
          name: createdPlayerName,
          draftRowId: draftRowId ?? '',
        });

        if (isSingleRoundMode && !tourId) {
          setAvailablePlayers((prev) =>
            upsertAvailablePlayer(prev, {
              id: createdPlayerId,
              name: createdPlayerName,
              userId: null,
              isTemporary: payload.isTemporary ?? true,
              isFriend: false,
            })
          );
          setPlayers((prev) =>
            appendPlayerRowIfMissing(prev, { id: createdPlayerId, name: createdPlayerName, scp: pendingScp }, MAX_PLAYERS)
          );
          if (draftRowId) {
            setDraftRows((prev) => prev.filter((row) => row.rowId !== draftRowId));
            setPlayerRowOpen((prev) => (prev === draftRowId ? null : prev));
          }
          setPlayerError(null);
          return;
        }

        setAvailablePlayers((prev) =>
          upsertAvailablePlayer(prev, {
            id: createdPlayerId,
            name: createdPlayerName,
            userId: null,
            isTemporary: payload.isTemporary ?? true,
            isFriend: false,
          })
        );
        setPlayers((prev) =>
          appendPlayerRowIfMissing(prev, { id: createdPlayerId, name: createdPlayerName, scp: pendingScp }, MAX_PLAYERS)
        );
        if (draftRowId) {
          setDraftRows((prev) => prev.filter((row) => row.rowId !== draftRowId));
          setPlayerRowOpen((prev) => (prev === draftRowId ? null : prev));
        } else {
          setQuickAddPlayerName('');
        }
        sendTourMemberAdd(createdPlayerId, createdPlayerName, draftRowId, pendingScp);
        setPlayerError(null);
        return;
      }
      if ((message as { type?: string }).type === 'tour.member.added') {
        const payload = message as TourMemberAddedEvent;
        if (tourId && payload.tourId && payload.tourId !== tourId) {
          return;
        }
        if (!payload.requestId || !pendingMemberAddByRequestIdRef.current.has(payload.requestId)) {
          return;
        }

        const pendingMember = pendingMemberAddByRequestIdRef.current.get(payload.requestId);
        pendingMemberAddByRequestIdRef.current.delete(payload.requestId);
        const pendingTimeout = pendingMemberAddTimeoutByRequestIdRef.current.get(payload.requestId);
        if (pendingTimeout) {
          clearTimeout(pendingTimeout);
          pendingMemberAddTimeoutByRequestIdRef.current.delete(payload.requestId);
        }
        syncPendingTemporaryPlayers();

        const memberPlayerId = payload.member?.playerId ?? pendingMember?.playerId ?? '';
        const memberName = (payload.member?.name ?? pendingMember?.name ?? '').trim();
        if (!memberPlayerId || !memberName) {
          setPlayerError(t('players.membersUpdateSendFailed'));
          if (pendingMember?.draftRowId) {
            setDraftStatus(pendingMember.draftRowId, 'idle');
          }
          return;
        }

        setAvailablePlayers((prev) =>
          upsertAvailablePlayer(prev, {
            id: memberPlayerId,
            name: memberName,
            userId: payload.member?.userId ?? null,
            isTemporary: payload.member?.isTemporary ?? true,
            isFriend: friendPlayerIdsRef.current.has(memberPlayerId),
          })
        );
        setPlayers((prev) =>
          appendPlayerRowIfMissing(
            prev,
            { id: memberPlayerId, name: memberName, scp: (pendingMember?.scp ?? '').trim() },
            MAX_PLAYERS
          )
        );
        if (pendingMember?.draftRowId) {
          setDraftRows((prev) => prev.filter((row) => row.rowId !== pendingMember.draftRowId));
          setPlayerRowOpen((prev) => (prev === pendingMember.draftRowId ? null : prev));
        }
        if (managedRoundId) {
          sendRoundMemberAdd(managedRoundId, memberPlayerId);
        }
        setPlayerError(null);

        // Now that this player is a confirmed tour member, a round.initiate that was held back
        // waiting for membership can proceed once every player is confirmed and no adds are pending.
        confirmedTourMemberIdsRef.current.add(memberPlayerId);
        const waitingRoundPayload = pendingRoundPayloadAfterMembershipRef.current;
        if (
          waitingRoundPayload &&
          pendingMemberAddByRequestIdRef.current.size === 0 &&
          waitingRoundPayload.players.every((player) => confirmedTourMemberIdsRef.current.has(player.id))
        ) {
          pendingRoundPayloadAfterMembershipRef.current = null;
          roundInitiateRequestIdRef.current = waitingRoundPayload.requestId;
          const initiateSocket = getSharedBffWebSocket(apiBaseUrl);
          initiateSocket.setAuthToken(user?.token ?? null);
          const initiated = initiateSocket.send(waitingRoundPayload);
          if (!initiated) {
            logToBff('players.round.initiate.queued', { tourId, requestId: waitingRoundPayload.requestId }, 'warn');
          } else {
            logToBff('players.round.initiate.sent', {
              tourId,
              requestId: waitingRoundPayload.requestId,
              players: waitingRoundPayload.players.length,
              afterMembership: true,
            });
          }
          if (roundAckTimeoutRef.current) {
            clearTimeout(roundAckTimeoutRef.current);
          }
          roundAckTimeoutRef.current = setTimeout(() => {
            if (roundInitiateRequestIdRef.current !== waitingRoundPayload.requestId) {
              return;
            }
            roundInitiateRequestIdRef.current = null;
            setSavingPlayers(false);
            setPlayerError(t('players.roundInitiateTimeout'));
            logToBff('players.round.initiate.timeout', { tourId, requestId: waitingRoundPayload.requestId }, 'warn');
          }, 12000);
        }
        return;
      }
      if ((message as { type?: string }).type === 'round.updated') {
        const payload = message as RoundUpdatedEvent;
        if (!managedRoundId || payload.roundId !== managedRoundId) {
          return;
        }
        if (payload.playDate) {
          setRoundPlayDate(payload.playDate);
        }
        if (payload.holesCount === 9 || payload.holesCount === 18) {
          setHolesCount(payload.holesCount);
        }
        if (payload.course?.id) {
          setSelectedCourseId(payload.course.id);
        }
        if (payload.course?.name) {
          setSelectedCourseName(payload.course.name);
        }
        return;
      }
      if ((message as { type?: string }).type === 'tour.created') {
        const payload = message as TourCreatedEvent;
        if (!pendingHiddenTourRequestIdRef.current || !payload.requestId || payload.requestId !== pendingHiddenTourRequestIdRef.current) {
          return;
        }
        pendingHiddenTourRequestIdRef.current = null;
        const createdTourId = payload.tourId ?? payload.TourId ?? null;
        if (!createdTourId) {
          setSavingPlayers(false);
          setPlayerError(t('players.missingTour'));
          return;
        }
        setTourId(createdTourId);
        setCurrentTourId(createdTourId);
        mergeNextMembersSnapshotRef.current = true;
        const membersRequestId = generateId();
        pendingTourMembersSnapshotRequestIdRef.current = membersRequestId;
        const membersSocket = getSharedBffWebSocket(apiBaseUrl);
        membersSocket.setAuthToken(user?.token ?? null);
        membersSocket.send({
          type: 'tour.members.request',
          requestId: membersRequestId,
          tourId: createdTourId,
        });

        if (pendingShareAfterHiddenTourRef.current) {
          setShareModalOpen(true);
          setShareModalLoading(true);
          setShareQrVisible(false);
          setShareModalError(null);
          setShareModalLink(null);
        }

        const queuedRoundPayload = pendingRoundPayloadAfterHiddenTourRef.current;
        if (!queuedRoundPayload) {
          setSavingPlayers(false);
          return;
        }

        const roundPayload: PendingRoundInitiatePayload = {
          ...queuedRoundPayload,
          tourId: createdTourId,
        };
        pendingRoundPayloadAfterHiddenTourRef.current = null;
        navigateOnRoundCreatedRef.current = true;
        roundInitiateRequestIdRef.current = roundPayload.requestId;
        const socketClient = getSharedBffWebSocket(apiBaseUrl);
        socketClient.setAuthToken(user?.token ?? null);
        const sent = socketClient.send(roundPayload);
        if (!sent) {
          logToBff('players.round.initiate.queued', { tourId: createdTourId, requestId: roundPayload.requestId }, 'warn');
        } else {
          logToBff('players.round.initiate.sent', { tourId: createdTourId, requestId: roundPayload.requestId, players: roundPayload.players.length });
        }

        if (roundAckTimeoutRef.current) {
          clearTimeout(roundAckTimeoutRef.current);
        }
        roundAckTimeoutRef.current = setTimeout(() => {
          if (roundInitiateRequestIdRef.current !== roundPayload.requestId) {
            return;
          }
          roundInitiateRequestIdRef.current = null;
          setSavingPlayers(false);
          setPlayerError(t('players.roundInitiateTimeout'));
          logToBff('players.round.initiate.timeout', { tourId: createdTourId, requestId: roundPayload.requestId }, 'warn');
        }, 12000);
        return;
      }
      if ((message as { type?: string }).type === 'round.initiate.rejected') {
        const payload = message as RoundInitiateRejectedEvent;
        if (roundInitiateRequestIdRef.current && payload.requestId && payload.requestId !== roundInitiateRequestIdRef.current) {
          return;
        }
        if (roundAckTimeoutRef.current) {
          clearTimeout(roundAckTimeoutRef.current);
          roundAckTimeoutRef.current = null;
        }
        roundInitiateRequestIdRef.current = null;
        navigateOnRoundCreatedRef.current = false;
        lastAutoRoundInitiateKeyRef.current = null;
        setSavingPlayers(false);
        const translated = payload.messageKey ? t(payload.messageKey) : '';
        const localizedMessage =
          translated && payload.messageKey && translated !== payload.messageKey
            ? translated
            : payload.message || t('players.roundInitiateRejected');
        setPlayerError(localizedMessage);
        logToBff('players.round.initiate.rejected', {
          requestId: payload.requestId,
          tourId: payload.tourId,
          code: payload.code,
          messageKey: payload.messageKey,
        });
        return;
      }
      if ((message as { type?: string }).type === 'round.created') {
        const payload = message as RoundCreatedEvent;
        const incomingRequestId = payload.requestId;
        if (roundInitiateRequestIdRef.current && incomingRequestId && incomingRequestId !== roundInitiateRequestIdRef.current) {
          return;
        }
        if (roundAckTimeoutRef.current) {
          clearTimeout(roundAckTimeoutRef.current);
          roundAckTimeoutRef.current = null;
        }
        roundInitiateRequestIdRef.current = null;
        setSavingPlayers(false);
        const incomingRoundId =
          payload.roundId && payload.roundId !== '00000000-0000-0000-0000-000000000000'
            ? payload.roundId
            : null;
        const incomingTourId =
          payload.tourId && payload.tourId !== '00000000-0000-0000-0000-000000000000'
            ? payload.tourId
            : null;
        if (incomingRoundId) {
          setManagedRoundId(incomingRoundId);
          setRoundId(incomingRoundId);
          const currentPlayerIds = players.map((player) => player.id).filter(Boolean);
          currentPlayerIds.forEach((playerId) => {
            syncedRoundMemberKeysRef.current.add(`${incomingRoundId}:${playerId}`);
          });
          lastRoundSettingsKeyRef.current = null;
        }
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        logToBff('players.round.created', { roundId: incomingRoundId, tourId: incomingTourId });
        const debugSuffix = debug ? '?debug=true' : '';
        if (incomingRoundId && navigateOnRoundCreatedRef.current) {
          navigateOnRoundCreatedRef.current = false;
          router.push(`/round/${incomingRoundId}/select-games${debugSuffix}`);
        }
      }
    });
    const unsubscribeError = socket.onError((msg) => {
      setPlayerError(msg);
      setSavingPlayers(false);
      roundInitiateRequestIdRef.current = null;
    });
    return () => {
      unsubscribe();
      unsubscribeError();
      if (roundAckTimeoutRef.current) {
        clearTimeout(roundAckTimeoutRef.current);
        roundAckTimeoutRef.current = null;
      }
      if (pendingCreatedCourseSelectionTimeoutRef.current) {
        clearTimeout(pendingCreatedCourseSelectionTimeoutRef.current);
        pendingCreatedCourseSelectionTimeoutRef.current = null;
      }
    };
  }, [apiBaseUrl, clearPendingTemporaryTracking, debug, isSingleRoundMode, logToBff, managedRoundId, players, requestFriendSnapshot, router, sendRoundMemberAdd, sendTourMemberAdd, setDraftStatus, setRoundId, sortByFriendPriority, syncPendingTemporaryPlayers, t, tourId, user?.id, user?.token]);

  useEffect(() => {
    return () => {
      clearPendingTemporaryTracking();
      pendingHiddenTourRequestIdRef.current = null;
      pendingRoundPayloadAfterHiddenTourRef.current = null;
      navigateOnRoundCreatedRef.current = false;
      lastAutoRoundInitiateKeyRef.current = null;
      lastRoundSettingsKeyRef.current = null;
      syncedRoundMemberKeysRef.current.clear();
    };
  }, [clearPendingTemporaryTracking]);

  useEffect(() => {
    if (!user?.token) {
      friendPlayerIdsRef.current = new Set();
      setFriendDirectory([]);
      return;
    }
    requestFriendSnapshot();
  }, [requestFriendSnapshot, user?.token]);

  useEffect(() => {
    if (!isNavigationReady || !user?.token) {
      return;
    }

    let cancelled = false;
    const loadPlatformPlayers = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players/platform`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        let platformPayload: Array<Record<string, unknown>> = [];
        if (response.ok) {
          platformPayload = (await response.json()) as Array<Record<string, unknown>>;
        }

        let allPlayersPayload: Array<Record<string, unknown>> = [];
        const allPlayersResponse = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (allPlayersResponse.ok) {
          const payload = (await allPlayersResponse.json()) as
            | Array<Record<string, unknown>>
            | { players?: Array<Record<string, unknown>>; Players?: Array<Record<string, unknown>> };
          allPlayersPayload = Array.isArray(payload)
            ? payload
            : (payload.players ?? payload.Players ?? []);
        }
        if (cancelled) {
          return;
        }

        const combinedPayload = [...platformPayload, ...allPlayersPayload];
        const normalized = combinedPayload
          .map((entry) => ({
            id: ((entry.id as string | undefined) ?? (entry.playerId as string | undefined) ?? '').trim(),
            name: ((entry.name as string | undefined) ?? '').trim(),
            userId: (entry.userId as string | null | undefined) ?? null,
          }))
          .filter((player) => player.id.length > 0 && player.name.length > 0);

        const handicapUpdates: Record<string, number> = {};
        combinedPayload.forEach((entry) => {
          const id = ((entry.id as string | undefined) ?? (entry.playerId as string | undefined) ?? '').trim();
          if (!id) return;
          const index = parseHandicapIndex(entry.handicap ?? entry.Handicap);
          if (index !== null) {
            handicapUpdates[id] = index;
          }
        });
        if (Object.keys(handicapUpdates).length > 0) {
          setHandicapIndexByPlayerId((prev) => ({ ...prev, ...handicapUpdates }));
        }

        setAvailablePlayers((prev) => {
          let next = prev;
          normalized.forEach((player) => {
            next = upsertAvailablePlayer(next, {
              id: player.id,
              name: player.name,
              userId: player.userId,
              isTemporary: false,
              isFriend: friendPlayerIdsRef.current.has(player.id),
            });
          });
          return sortByFriendPriority(next);
        });
      } catch {
        // Ignore load errors; tour members and friend snapshot still provide options.
      }
    };

    void loadPlatformPlayers();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, sortByFriendPriority, user?.token]);

  const updateRoundStatus = useCallback(
    (roundId: string, status: string) => {
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      socket.send({ type: 'round.status.update', roundId, status });
      setActiveRounds((prev) =>
        status === 'cancelled'
          ? prev.filter((round) => round.id !== roundId)
          : prev.map((round) =>
              round.id === roundId ? { ...round, status, statusUpdatedAt: new Date().toISOString() } : round
            )
      );
      setFinishedRounds((prev) =>
        status === 'cancelled' ? prev.filter((round) => round.id !== roundId) : prev
      );
      setOpenRoundMenuId(null);
    },
    [apiBaseUrl, user?.token]
  );

  const formatRoundStatus = useCallback(
    (round: TourRoundSummary) => {
      if (!round.status) return null;
      const normalized = round.status.toLowerCase();
      if (normalized === 'active' || normalized === 'finished') {
        const timestamp = round.statusUpdatedAt ?? round.createdAt;
        if (!timestamp) {
          return t('players.roundStatus', { status: round.status });
        }
        const dateLabel = new Date(timestamp).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        return t('players.roundStatusWithDate', { status: round.status, date: dateLabel });
      }
      return t('players.roundStatus', { status: round.status });
    },
    []
  );

  const truncateRoundName = useCallback((value?: string | null) => {
    const name = value ?? '';
    if (name.length <= 27) return name;
    return `${name.slice(0, 27)}…`;
  }, []);

  // Disabled courses must not be selectable at all — drop them before the picker/search ever sees them.
  const selectableCourses = useMemo(
    () => courses.filter((course) => course.visibility !== 'disabled'),
    [courses],
  );
  const filteredCourses = useMemo(() => {
    const query = courseSearchQuery.trim().toLowerCase();
    if (!query) return selectableCourses;
    return selectableCourses.filter((course) => {
      const name = course.name.toLowerCase();
      const address = (course.address ?? '').toLowerCase();
      const city = (course.city ?? '').toLowerCase();
      return name.includes(query) || address.includes(query) || city.includes(query);
    });
  }, [courseSearchQuery, selectableCourses]);
  const selectedCourse = courses.find((course) => course.id === selectedCourseId) ?? null;
  const commitCourseFromQuery = useCallback(() => {
    const normalized = courseSearchQuery.trim().toLowerCase();
    if (!normalized) {
      if (selectedCourse) {
        setCourseSearchQuery(selectedCourse.name);
      }
      setCoursePickerOpen(false);
      return;
    }

    const exact = selectableCourses.find((course) => course.name.trim().toLowerCase() === normalized);
    const match = exact ?? filteredCourses[0] ?? null;
    if (match) {
      setSelectedCourseId(match.id);
      setSelectedCourseName(match.name);
      setCourseSearchQuery(match.name);
      setCoursePickerOpen(false);
      return;
    }

    setCoursePickerOpen(true);
  }, [courseSearchQuery, selectableCourses, filteredCourses, selectedCourse]);

  const selectCourse = useCallback((id: string, name: string) => {
    setSelectedCourseId(id);
    setSelectedCourseName(name);
    setCourseSearchQuery(name);
    setCoursePickerOpen(false);
  }, []);

  const showRecentCourses = courseSearchQuery.trim().length === 0 && recentCourses.length > 0;

  useEffect(() => {
    if (coursePickerOpen) return;
    setCourseSearchQuery(selectedCourse?.name ?? selectedCourseName);
  }, [coursePickerOpen, selectedCourse?.name, selectedCourseName]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;

  const addPlayerRow = () => {
    setPlayerError(null);
    if (!canAddPlayerRow) return;
    const rowId = `draft-${generateId()}`;
    setDraftRows((prev) => [...prev, { kind: 'draft', rowId, query: '', scp: '', temporarySelected: false, status: 'idle' }]);
    setPlayerRowOpen(rowId);
  };

  const removePlayerRow = (index: number) => {
    setPlayerError(null);
    setPlayers((prev) => {
      if (currentUserPlayerId && prev[index]?.id === currentUserPlayerId) {
        return prev;
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const removeMembershipRow = useCallback(
    (row: PlayerMembershipRow) => {
      if (row.kind === 'existing') {
        const index = players.findIndex((player) => player.id === row.playerId);
        if (index >= 0) {
          removePlayerRow(index);
        }
        return;
      }
      setDraftRows((prev) => prev.filter((draft) => draft.rowId !== row.rowId));
      setPlayerRowOpen((prev) => (prev === row.rowId ? null : prev));
      pendingTemporaryCreateByRequestIdRef.current.forEach((mappedDraft, requestId) => {
        if (mappedDraft.rowId === row.rowId) {
          pendingTemporaryCreateByRequestIdRef.current.delete(requestId);
          const timeout = pendingTemporaryCreateTimeoutByRequestIdRef.current.get(requestId);
          if (timeout) {
            clearTimeout(timeout);
            pendingTemporaryCreateTimeoutByRequestIdRef.current.delete(requestId);
          }
        }
      });
      syncPendingTemporaryPlayers();
    },
    [players, syncPendingTemporaryPlayers]
  );

  const onDraftQueryChange = useCallback((rowId: string, value: string) => {
    setDraftRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              query: value,
              selectedPlayerId: undefined,
              selectedPlayerName: undefined,
              scp: '',
              temporarySelected: false,
              status: 'idle',
            }
          : row
      )
    );
    setPlayerRowOpen(rowId);
  }, []);

  const onDraftSelectOption = useCallback((rowId: string, player: AvailableMember) => {
    setPlayerError(null);
    setDraftRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              selectedPlayerId: player.id,
              selectedPlayerName: player.name,
              query: player.name,
              scp: '',
              temporarySelected: false,
              status: 'idle',
            }
          : row
      )
    );
    setPlayerRowOpen(null);
  }, []);

  const clearScpValidationErrorIfResolved = useCallback(
    (
      nextPlayers: Array<{ id: string; name: string; scp: string }>,
      nextDraftRows: Array<{
        rowId: string;
        query: string;
        scp?: string;
        selectedPlayerId?: string;
        temporarySelected?: boolean;
      }>
    ) => {
      if (playerError !== t('home.scpRequired')) return;

      const hasMissingPlayerScp = nextPlayers.some((player) => !player.scp.trim());
      const hasMissingDraftScp = nextDraftRows.some((row) => {
        const hasManualDraftName = row.query.trim().length > 0;
        const requiresScp = hasManualDraftName && !row.selectedPlayerId && !row.temporarySelected;
        return requiresScp && !(row.scp ?? '').trim();
      });

      if (!hasMissingPlayerScp && !hasMissingDraftScp) {
        setPlayerError(null);
      }
    },
    [playerError, t]
  );

  const onDraftScpChange = useCallback((rowId: string, value: string) => {
    setDraftRows((prev) => {
      const nextDraftRows = prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              scp: value,
            }
          : row
      );
      clearScpValidationErrorIfResolved(players, nextDraftRows);
      return nextDraftRows;
    });
  }, [clearScpValidationErrorIfResolved, players]);

  const onPlayerScpChange = useCallback((index: number, value: string) => {
    setPlayers((prev) => {
      const nextPlayers = prev.map((player, idx) => (idx === index ? { ...player, scp: value } : player));
      clearScpValidationErrorIfResolved(nextPlayers, draftRows);
      return nextPlayers;
    });
  }, [clearScpValidationErrorIfResolved, draftRows]);

  const onToggleTemporary = useCallback((rowId: string) => {
    setDraftRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              temporarySelected: !row.temporarySelected,
              selectedPlayerId: undefined,
              selectedPlayerName: undefined,
              scp: row.temporarySelected ? '' : row.scp ?? '',
              status: 'idle',
            }
          : row
      )
    );
  }, []);

  const confirmDraftRow = useCallback(
    (rowId: string) => {
      if (savingPlayers) return;
      const row = draftRows.find((candidate) => candidate.rowId === rowId);
      if (!row) return;
      const query = row.query.trim();
      const draftScp = (row.scp ?? '').trim();

      if (row.selectedPlayerId) {
        const alreadyMember = players.some((candidate) => candidate.id === row.selectedPlayerId);
        if (alreadyMember) {
          setPlayerError(t('tourEdit.playerAlreadyInTour'));
          return;
        }
        const resolvedName = (row.selectedPlayerName ?? query).trim();
        if (isSingleRoundMode && !tourId) {
          if (!draftScp) {
            setPlayerError(t('home.scpRequired'));
            return;
          }
          setPlayerError(null);
          setAvailablePlayers((prev) =>
            upsertAvailablePlayer(prev, {
              id: row.selectedPlayerId!,
              name: resolvedName,
              isTemporary: false,
              isFriend: friendPlayerIdsRef.current.has(row.selectedPlayerId!),
            })
          );
          setPlayers((prev) => appendPlayerRowIfMissing(prev, { id: row.selectedPlayerId!, name: resolvedName, scp: draftScp }, MAX_PLAYERS));
          setDraftRows((prev) => prev.filter((draft) => draft.rowId !== rowId));
          setPlayerRowOpen((prev) => (prev === rowId ? null : prev));
          return;
        }
        setPlayerError(null);
        setDraftStatus(rowId, 'pending');
        const sent = sendTourMemberAdd(row.selectedPlayerId, row.selectedPlayerName ?? query, rowId);
        if (!sent) {
          setDraftStatus(rowId, 'idle');
        }
        return;
      }

      if (!query) {
        setPlayerError(t('tourEdit.playerConfirmEmpty'));
        return;
      }
      if (!draftScp) {
        setPlayerError(t('home.scpRequired'));
        return;
      }
      if (!user?.token) {
        setPlayerError(t('players.missingTour'));
        return;
      }

      const normalized = query.toLowerCase();
      const exactMatch = availablePlayers.find((player) => player.name.trim().toLowerCase() === normalized);
      if (exactMatch) {
        const alreadyMember = players.some((candidate) => candidate.id === exactMatch.id);
        if (alreadyMember) {
          setPlayerError(t('tourEdit.playerAlreadyInTour'));
          return;
        }
        if (isSingleRoundMode && !tourId) {
          setPlayerError(null);
          setAvailablePlayers((prev) =>
            upsertAvailablePlayer(prev, {
              id: exactMatch.id,
              name: exactMatch.name,
              userId: exactMatch.userId ?? null,
              isTemporary: exactMatch.isTemporary ?? false,
              isFriend: friendPlayerIdsRef.current.has(exactMatch.id),
            })
          );
          setPlayers((prev) => appendPlayerRowIfMissing(prev, { id: exactMatch.id, name: exactMatch.name, scp: draftScp }, MAX_PLAYERS));
          setDraftRows((prev) => prev.filter((draft) => draft.rowId !== rowId));
          setPlayerRowOpen((prev) => (prev === rowId ? null : prev));
          return;
        }
        setPlayerError(null);
        setDraftStatus(rowId, 'pending');
        const sent = sendTourMemberAdd(exactMatch.id, exactMatch.name, rowId, draftScp);
        if (!sent) {
          setDraftStatus(rowId, 'idle');
        }
        return;
      }

      setPlayerError(null);
      setDraftStatus(rowId, 'pending');
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      const requestId = normalizeRequestId(generateId());
      pendingTemporaryCreateByRequestIdRef.current.set(requestId, { rowId, scp: draftScp, name: query });
      const timeout = setTimeout(() => {
        if (!pendingTemporaryCreateByRequestIdRef.current.has(requestId)) {
          return;
        }
        pendingTemporaryCreateByRequestIdRef.current.delete(requestId);
        pendingTemporaryCreateTimeoutByRequestIdRef.current.delete(requestId);
        syncPendingTemporaryPlayers();
        setDraftStatus(rowId, 'idle');
        setPlayerError(t('players.temporaryCreateFailed'));
        logToBff(
          'players.temporary.create.timeout',
          { requestId, rowId, name: query, pendingCount: pendingTemporaryCreateByRequestIdRef.current.size },
          'warn'
        );
      }, 12000);
      pendingTemporaryCreateTimeoutByRequestIdRef.current.set(requestId, timeout);
      syncPendingTemporaryPlayers();
      logToBff('players.temporary.create.send', { requestId, rowId, name: query });
      const sent = socket.send({
        type: 'player.temporary.create',
        requestId,
        name: query,
      });
      if (!sent) {
        logToBff('players.temporary.create.queued', { requestId, rowId, name: query }, 'warn');
      }
    },
    [apiBaseUrl, availablePlayers, draftRows, isSingleRoundMode, players, savingPlayers, sendTourMemberAdd, setDraftStatus, syncPendingTemporaryPlayers, t, tourId, user?.token]
  );

  const addKnownPlayer = useCallback(
    (playerId: string, playerName: string, userId?: string | null, scp?: string) => {
      if (!playerId) return false;
      if (players.some((player) => player.id === playerId)) {
        setPlayerError(t('tourEdit.playerAlreadyInTour'));
        return false;
      }
      const normalizedScp = (scp ?? '').trim();
      if (isSingleRoundMode && !tourId) {
        setAvailablePlayers((prev) =>
          upsertAvailablePlayer(prev, {
            id: playerId,
            name: playerName,
            userId: userId ?? null,
            isTemporary: false,
            isFriend: true,
          })
        );
        setPlayers((prev) => appendPlayerRowIfMissing(prev, { id: playerId, name: playerName, scp: normalizedScp }, MAX_PLAYERS));
      } else {
        const sent = sendTourMemberAdd(playerId, playerName, undefined, normalizedScp);
        if (!sent) {
          return false;
        }
      }
      setQuickAddPlayerName('');
      setQuickAddScp('');
      return true;
    },
    [isSingleRoundMode, players, sendTourMemberAdd, t, tourId]
  );

  const addPlayerByName = useCallback(() => {
    if (savingPlayers || !canAddPlayerRow) return;
    const query = quickAddPlayerName.trim();
    if (!query) return;

    if (!quickAddScp.trim()) {
      setPlayerError(t('home.scpRequired'));
      return;
    }

    setPlayerError(null);
    const normalized = query.toLowerCase();
    const friendSnapshotMatch =
      friendDirectory.find((friend) => {
        const name = (friend.playerName ?? friend.name ?? '').trim().toLowerCase();
        const nickname = (friend.nickname ?? '').trim().toLowerCase();
        return name === normalized || nickname === normalized;
      })
      ?? friendDirectory.find((friend) => {
        const name = (friend.playerName ?? friend.name ?? '').trim().toLowerCase();
        const nickname = (friend.nickname ?? '').trim().toLowerCase();
        return name.includes(normalized) || nickname.includes(normalized);
      })
      ?? null;

    const friendMatch =
      (friendSnapshotMatch?.playerId
        ? {
            id: friendSnapshotMatch.playerId,
            name: (friendSnapshotMatch.playerName ?? friendSnapshotMatch.name ?? '').trim() || query,
            userId: friendSnapshotMatch.userId ?? null,
          }
        : null)
      ?? availableMembersForEditor.find((member) => member.name.trim().toLowerCase() === normalized)
      ?? availableMembersForEditor.find((member) => member.name.toLowerCase().includes(normalized))
      ?? null;

    const quickScp = quickAddScp.trim();

    if (friendMatch) {
      addKnownPlayer(friendMatch.id, friendMatch.name, friendMatch.userId ?? null, quickScp);
      return;
    }

    if (!user?.token) {
      setPlayerError(t('players.missingTour'));
      return;
    }

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const requestId = normalizeRequestId(generateId());
    pendingTemporaryCreateByRequestIdRef.current.set(requestId, { rowId: '', scp: quickScp, name: query });
    const timeout = setTimeout(() => {
      if (!pendingTemporaryCreateByRequestIdRef.current.has(requestId)) {
        return;
      }
      pendingTemporaryCreateByRequestIdRef.current.delete(requestId);
      pendingTemporaryCreateTimeoutByRequestIdRef.current.delete(requestId);
      syncPendingTemporaryPlayers();
      setPlayerError(t('players.temporaryCreateFailed'));
      logToBff(
        'players.temporary.create.timeout',
        { requestId, name: query, pendingCount: pendingTemporaryCreateByRequestIdRef.current.size },
        'warn'
      );
    }, 12000);
    pendingTemporaryCreateTimeoutByRequestIdRef.current.set(requestId, timeout);
    syncPendingTemporaryPlayers();
    logToBff('players.temporary.create.send', { requestId, name: query });
    const sent = socket.send({
      type: 'player.temporary.create',
      requestId,
      name: query,
    });
    if (!sent) {
      logToBff('players.temporary.create.queued', { requestId, name: query }, 'warn');
    }
  }, [
    addKnownPlayer,
    apiBaseUrl,
    availableMembersForEditor,
    canAddPlayerRow,
    friendDirectory,
    logToBff,
    quickAddPlayerName,
    quickAddScp,
    savingPlayers,
    syncPendingTemporaryPlayers,
    t,
    user?.token,
  ]);

  const quickAddSuggestions = useMemo<AvailableMember[]>(() => {
    const query = quickAddPlayerName.trim().toLowerCase();
    if (!query || savingPlayers || !canAddPlayerRow) {
      return [];
    }

    const selectedIds = new Set(players.map((player) => player.id));
    return availableMembersForEditor
      .filter((member) => !selectedIds.has(member.id))
      .map((member) => ({
        member,
        normalizedName: member.name.trim().toLowerCase(),
      }))
      .filter((entry) => entry.normalizedName.includes(query))
      .sort((a, b) => {
        const aStartsWith = a.normalizedName.startsWith(query);
        const bStartsWith = b.normalizedName.startsWith(query);
        if (aStartsWith !== bStartsWith) {
          return aStartsWith ? -1 : 1;
        }
        return a.member.name.localeCompare(b.member.name);
      })
      .slice(0, 6)
      .map((entry) => entry.member);
  }, [availableMembersForEditor, canAddPlayerRow, players, quickAddPlayerName, savingPlayers]);

  const resolveInviteLink = useCallback(
    async (targetTourId: string) => {
      return resolveTourInviteLink({
        apiBaseUrl,
        tourId: targetTourId,
        authToken: user?.token,
      });
    },
    [apiBaseUrl, user?.token]
  );

  useEffect(() => {
    if (!pendingShareAfterHiddenTourRef.current || !tourId) {
      return;
    }
    pendingShareAfterHiddenTourRef.current = false;
    setShareModalOpen(true);
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalLink(null);
    setShareQrVisible(false);
    void resolveInviteLink(tourId)
      .then((inviteLink) => {
        setShareModalLink(inviteLink);
      })
      .catch((shareError) => {
        setShareModalError(t('tournaments.shareLoadError'));
        logToBff(
          'players.round.share.link.error',
          {
            tourId,
            message: shareError instanceof Error ? shareError.message : String(shareError),
          },
          'warn'
        );
      })
      .finally(() => {
        setShareModalLoading(false);
      });
  }, [logToBff, resolveInviteLink, t, tourId]);

  const handleOpenRoundShare = useCallback(async () => {
    if (!tourId) {
      if (!isSingleRoundMode || !user?.token) {
        setShareModalError(t('players.missingTour'));
        return;
      }
      pendingShareAfterHiddenTourRef.current = true;
      setShareModalOpen(true);
      setShareModalLoading(true);
      setShareModalError(null);
      setShareModalLink(null);
      setShareQrVisible(false);
      if (pendingHiddenTourRequestIdRef.current) {
        return;
      }
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      const hiddenTourRequestId = generateId();
      pendingHiddenTourRequestIdRef.current = hiddenTourRequestId;
      const sent = socket.send({
        type: 'tour.initiate',
        requestId: hiddenTourRequestId,
        tourName: t('players.singleRoundHiddenTourName'),
        playerIds: players.map((player) => player.id),
        isHidden: true,
      });
      if (!sent) {
        pendingHiddenTourRequestIdRef.current = null;
        pendingShareAfterHiddenTourRef.current = false;
        setShareModalLoading(false);
        setShareModalError(t('players.hiddenTourInitiateFailed'));
      }
      return;
    }

    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalLink(null);
    setShareQrVisible(false);
    setShareModalOpen(true);

    try {
      const inviteLink = await resolveInviteLink(tourId);
      setShareModalLink(inviteLink);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      logToBff('players.round.share.link.error', {
        tourId,
        message: shareError instanceof Error ? shareError.message : String(shareError),
      }, 'warn');
    } finally {
      setShareModalLoading(false);
    }
  }, [apiBaseUrl, isSingleRoundMode, logToBff, players, resolveInviteLink, t, tourId, user?.token]);

  const shareJoinCode = useMemo(() => {
    if (!shareModalLink) {
      return '';
    }
    try {
      const token = decodeURIComponent(shareModalLink.split('/').pop() ?? '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
      if (!token) {
        return '';
      }
      const compact = token.slice(0, 6);
      if (compact.length <= 3) {
        return compact;
      }
      return `${compact.slice(0, 3)} ${compact.slice(3)}`;
    } catch {
      return '';
    }
  }, [shareModalLink]);

  const handleShareRoundInviteLink = useCallback(async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: selectedCourse?.name ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  }, [selectedCourse?.name, shareModalLink, t]);

  const startRoundInitiation = useCallback((options?: { navigateOnSuccess?: boolean; suppressErrors?: boolean; background?: boolean }) => {
    const navigateOnSuccess = options?.navigateOnSuccess ?? true;
    const suppressErrors = options?.suppressErrors ?? false;
    const background = options?.background ?? false;

    if (managedRoundId) {
      navigateOnRoundCreatedRef.current = false;
      if (navigateOnSuccess) {
        const debugSuffix = debug ? '?debug=true' : '';
        router.push(`/round/${managedRoundId}/select-games${debugSuffix}`);
      }
      return;
    }

    if (roundInitiateRequestIdRef.current) {
      navigateOnRoundCreatedRef.current = navigateOnRoundCreatedRef.current || navigateOnSuccess;
      return;
    }

    if (pendingTemporaryPlayers > 0) {
      if (!suppressErrors) {
        setPlayerError(t('players.temporaryCreatePending'));
      }
      return;
    }

    const selectedPlayers = players
      .map((player) => ({ ...player, name: player.name.trim(), scp: player.scp.trim() }))
      .filter((player) => player.name.length);
    if (selectedPlayers.length === 0) {
      if (!suppressErrors) {
        setPlayerError(t('home.validationRequired'));
      }
      logToBff('players.validation.missingPlayers', { playerCount: players.length });
      return;
    }
    if (selectedPlayers.length > MAX_PLAYERS) {
      if (!suppressErrors) {
        setPlayerError(t('home.maxPlayers'));
      }
      return;
    }
    if (selectedPlayers.some((player) => !availablePlayerIdSet.has(player.id))) {
      if (!suppressErrors) {
        setPlayerError(t('players.selectFromMembership'));
      }
      return;
    }
    if (!background && selectedPlayers.some((player) => !player.scp)) {
      if (!suppressErrors) {
        setPlayerError(t('home.scpRequired'));
      }
      logToBff('players.validation.missingScp', { playerCount: selectedPlayers.length });
      return;
    }
    if (!selectedCourse) {
      if (!suppressErrors) {
        setCourseError(t('course.select'));
      }
      logToBff('players.validation.missingCourse', {});
      return;
    }
    if (!tourId && !isSingleRoundMode) {
      if (!suppressErrors) {
        setPlayerError(t('players.missingTour'));
      }
      return;
    }

    if (!background) {
      logToBff('players.handleSelectGames.clicked', { tourId, playerCount: selectedPlayers.length });
      setPlayerError(null);
      setCourseError(null);
      setSavingPlayers(true);
    }

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const roundRequestId = generateId();
    const normalizedRoundName = roundName.trim() || selectedCourse.name;
    const selectedTee = courseTees.find((tee) => tee.id === selectedTeeId) ?? null;
    const roundPayload: PendingRoundInitiatePayload = {
      type: 'round.initiate',
      requestId: roundRequestId,
      tourId: tourId ?? '',
      name: normalizedRoundName,
      description: roundDescription.trim() || undefined,
      holesCount,
      playDate: roundPlayDate || todayLocalDate(),
      players: selectedPlayers.map((player) => ({
        id: player.id,
        scp: player.scp,
      })),
      course: {
        ...selectedCourse,
        selectedTeeId: selectedTee?.id ?? null,
        selectedTeeName: selectedTee?.name ?? null,
      },
      gameDefinitionIds: [],
    };

    if (!tourId) {
      pendingRoundPayloadAfterHiddenTourRef.current = roundPayload;
      navigateOnRoundCreatedRef.current = navigateOnSuccess;
      if (pendingHiddenTourRequestIdRef.current) {
        return;
      }
      const hiddenTourRequestId = generateId();
      pendingHiddenTourRequestIdRef.current = hiddenTourRequestId;
      const tourSent = socket.send({
        type: 'tour.initiate',
        requestId: hiddenTourRequestId,
        tourName: t('players.singleRoundHiddenTourName'),
        playerIds: selectedPlayers.map((player) => player.id),
        isHidden: true,
      });
      if (!tourSent && !background) {
        setSavingPlayers(false);
        pendingHiddenTourRequestIdRef.current = null;
        pendingRoundPayloadAfterHiddenTourRef.current = null;
        setPlayerError(t('players.hiddenTourInitiateFailed'));
      }
      return;
    }

    // Single-round mode uses a hidden tour, and tour-service rejects a round whose players aren't
    // members of it. The hidden tour is created lazily/empty and members are added asynchronously, so
    // before initiating make sure every selected player is a confirmed member — add any that aren't
    // (AddPlayer is idempotent server-side) and defer the initiate until their tour.member.added acks
    // arrive (handled in the tour.member.added listener).
    if (isSingleRoundMode) {
      const unconfirmed = selectedPlayers.filter(
        (player) => !confirmedTourMemberIdsRef.current.has(player.id)
      );
      if (unconfirmed.length > 0) {
        const pendingPlayerIds = new Set(
          Array.from(pendingMemberAddByRequestIdRef.current.values()).map((entry) => entry.playerId)
        );
        pendingRoundPayloadAfterMembershipRef.current = roundPayload;
        navigateOnRoundCreatedRef.current = navigateOnSuccess;
        unconfirmed
          .filter((player) => !pendingPlayerIds.has(player.id))
          .forEach((player) => sendTourMemberAdd(player.id, player.name, undefined, player.scp));
        logToBff(
          'players.round.initiate.awaitingMembership',
          { tourId, requestId: roundRequestId, unconfirmed: unconfirmed.length },
          'warn'
        );
        return;
      }
    }

    pendingRoundPayloadAfterMembershipRef.current = null;
    roundInitiateRequestIdRef.current = roundRequestId;
    navigateOnRoundCreatedRef.current = navigateOnSuccess;
    const sent = socket.send(roundPayload);
    if (!sent) {
      logToBff('players.round.initiate.queued', { tourId, requestId: roundRequestId }, 'warn');
    }

    if (roundAckTimeoutRef.current) {
      clearTimeout(roundAckTimeoutRef.current);
    }
    roundAckTimeoutRef.current = setTimeout(() => {
      if (roundInitiateRequestIdRef.current !== roundRequestId) {
        return;
      }
      roundInitiateRequestIdRef.current = null;
      navigateOnRoundCreatedRef.current = false;
      lastAutoRoundInitiateKeyRef.current = null;
      setSavingPlayers(false);
      if (!background) {
        setPlayerError(t('players.roundInitiateTimeout'));
      }
      logToBff('players.round.initiate.timeout', { tourId, requestId: roundRequestId }, 'warn');
    }, 12000);
    logToBff('players.round.initiate.sent', { tourId, requestId: roundRequestId, players: selectedPlayers.length, background });
  }, [
    availablePlayerIdSet,
    courseTees,
    debug,
    holesCount,
    isSingleRoundMode,
    logToBff,
    pendingTemporaryPlayers,
    players,
    roundDescription,
    roundName,
    roundPlayDate,
    selectedCourse,
    selectedTeeId,
    sendTourMemberAdd,
    t,
    managedRoundId,
    router,
    tourId,
    user?.token,
  ]);

  useEffect(() => {
    if (managedRoundId) {
      return;
    }
    if (!tourId || !selectedCourse || pendingTemporaryPlayers > 0 || roundInitiateRequestIdRef.current) {
      return;
    }
    const selectedPlayers = players
      .map((player) => ({ id: player.id, name: player.name.trim(), scp: player.scp.trim() }))
      .filter((player) => player.name.length > 0 && player.id.length > 0);
    if (selectedPlayers.length === 0 || selectedPlayers.length > MAX_PLAYERS) {
      return;
    }
    if (selectedPlayers.some((player) => !availablePlayerIdSet.has(player.id))) {
      return;
    }
    // Wait until every player has a playing handicap (scp) before auto-creating
    // the round. The scp is precalculated asynchronously (WHS index + tee), so
    // creating too early persists an empty SHCP that can never be corrected
    // afterwards (the round is created only once). This mirrors the foreground
    // "Select games" validation which also requires an scp for every player.
    if (selectedPlayers.some((player) => !player.scp)) {
      return;
    }

    const autoKey = [
      tourId,
      selectedCourse.id,
      String(holesCount),
      roundPlayDate,
      selectedPlayers.map((player) => `${player.id}:${player.scp}`).join('|'),
    ].join('::');
    if (lastAutoRoundInitiateKeyRef.current === autoKey) {
      return;
    }

    lastAutoRoundInitiateKeyRef.current = autoKey;
    startRoundInitiation({ navigateOnSuccess: false, suppressErrors: true, background: true });
  }, [availablePlayerIdSet, holesCount, managedRoundId, pendingTemporaryPlayers, players, roundPlayDate, selectedCourse, startRoundInitiation, tourId]);

  useEffect(() => {
    if (!managedRoundId || !user?.token || !selectedCourse) {
      return;
    }

    const settingsKey = `${managedRoundId}:${selectedCourse.id}:${holesCount}:${roundPlayDate}`;
    if (lastRoundSettingsKeyRef.current === settingsKey) {
      return;
    }
    lastRoundSettingsKeyRef.current = settingsKey;

    const payload = buildRoundUpdateMessage({
      roundId: managedRoundId,
      course: {
        id: selectedCourse.id,
        name: selectedCourse.name,
        address: selectedCourse.address,
        lat: selectedCourse.lat,
        lon: selectedCourse.lon,
      },
      holesCount,
      playDate: parseDateInput(roundPlayDate),
    });

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    const sent = socket.send(payload);
    if (!sent) {
      logToBff('players.round.update.queued', { roundId: managedRoundId, holesCount, playDate: roundPlayDate }, 'warn');
    } else {
      logToBff('players.round.update.sent', { roundId: managedRoundId, holesCount, playDate: roundPlayDate });
    }
  }, [apiBaseUrl, holesCount, logToBff, managedRoundId, roundPlayDate, selectedCourse, user?.token]);

  // Once a round exists, persist any later change to a player's playing handicap
  // (scp) to the player-service so the value survives navigation and shows up on
  // the Round settings screen. round.initiate already carries the scp at creation
  // time, so the first signature we observe is seeded (not re-sent); only genuine
  // edits afterwards trigger a PUT.
  useEffect(() => {
    if (!managedRoundId || !user?.token) {
      if (!managedRoundId) {
        lastSyncedShcpSignatureRef.current = null;
      }
      return;
    }

    const entries = players
      .map((player) => ({ id: player.id, scp: player.scp.trim() }))
      .filter((player) => player.id.length > 0);
    if (entries.length === 0) {
      return;
    }

    const signature = entries.map((player) => `${player.id}:${player.scp}`).join('|');
    if (lastSyncedShcpSignatureRef.current === null) {
      // First observation after the round was created — this scp was already
      // persisted by round.initiate, so seed the baseline without re-sending.
      lastSyncedShcpSignatureRef.current = signature;
      return;
    }
    if (lastSyncedShcpSignatureRef.current === signature) {
      return;
    }
    lastSyncedShcpSignatureRef.current = signature;

    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${managedRoundId}/players/shcp`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ players: entries }),
        });
        if (!response.ok) {
          logToBff('players.round.shcp.sync.failed', { roundId: managedRoundId, status: response.status }, 'warn');
        } else if (!cancelled) {
          logToBff('players.round.shcp.sync.applied', { roundId: managedRoundId, players: entries.length });
        }
      } catch (err) {
        logToBff(
          'players.round.shcp.sync.error',
          { roundId: managedRoundId, message: err instanceof Error ? err.message : String(err) },
          'warn',
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, logToBff, managedRoundId, players, user?.token]);

  const handleSelectGames = useCallback(() => {
    // Commit any name still sitting in the quick-add row so it joins the round.
    const pendingQuickAddName = quickAddPlayerName.trim();
    if (pendingQuickAddName) {
      if (!quickAddScp.trim()) {
        setPlayerError(t('home.scpRequired'));
        return;
      }
      setPendingRoundStartAfterDraftSave(true);
      addPlayerByName();
      return;
    }
    if (draftRows.length > 0) {
      setPendingRoundStartAfterDraftSave(true);
      draftRows.forEach((row) => {
        if (row.status !== 'pending') {
          confirmDraftRow(row.rowId);
        }
      });
      return;
    }
    startRoundInitiation({ navigateOnSuccess: true, suppressErrors: false, background: false });
  }, [addPlayerByName, confirmDraftRow, draftRows, quickAddPlayerName, quickAddScp, startRoundInitiation, t]);

  const handleAddCourse = useCallback(() => {
    pendingCreatedCourseSelectionRef.current = true;
    if (pendingCreatedCourseSelectionTimeoutRef.current) {
      clearTimeout(pendingCreatedCourseSelectionTimeoutRef.current);
    }
    pendingCreatedCourseSelectionTimeoutRef.current = setTimeout(() => {
      pendingCreatedCourseSelectionRef.current = false;
      pendingCreatedCourseSelectionTimeoutRef.current = null;
    }, 2 * 60 * 1000);
    setCoursePickerOpen(false);
    const targetRoundId = managedRoundId ?? roundId;
    if (targetRoundId) {
      router.push(`/config-course?roundId=${encodeURIComponent(targetRoundId)}&closeOnSave=1`);
      return;
    }
    router.push('/config-course?closeOnSave=1');
  }, [managedRoundId, roundId, router]);

  const openAddTee = useCallback(() => {
    setAddTeeName('');
    setAddTeeCourseRating('');
    setAddTeeSlopeRating('');
    setAddTeeError(null);
    setAddTeeOpen(true);
  }, []);

  // Persist a new tee to the selected course. The backend upsert replaces the whole course, so we
  // re-load the course (holes + existing tees), append the new tee, and send everything back. Only
  // the name is required; course rating / slope default to neutral values so a name-only tee still
  // survives the backend's rating>0/slope>0/par>0 filter. On success we reload the tees and select
  // the new one for this round.
  const handleCreateTee = useCallback(async () => {
    if (!selectedCourseId) {
      setAddTeeError(t('players.addTeeNeedsCourse'));
      return;
    }
    const name = addTeeName.trim();
    if (!name) {
      setAddTeeError(t('players.addTeeNameRequired'));
      return;
    }
    if (!user?.token || !user?.id) {
      setAddTeeError(t('players.addTeeFailed'));
      return;
    }

    setAddTeeSaving(true);
    setAddTeeError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${selectedCourseId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error(`course load failed (${response.status})`);
      }
      const course = (await response.json()) as GeoCourseDetailResponse;

      const holes = mapCourseDetailHoles(course);
      const existingTees = mapCourseDetailTees(course);
      const coursePar =
        holes.reduce((sum, hole) => sum + hole.par, 0)
        || existingTees.find((tee) => tee.par > 0)?.par
        || 0;

      const parsedCourseRating = Number.parseFloat(addTeeCourseRating);
      const courseRating =
        Number.isFinite(parsedCourseRating) && parsedCourseRating > 0 ? parsedCourseRating : coursePar;
      const parsedSlope = Number.parseInt(addTeeSlopeRating, 10);
      const slopeRating =
        Number.isFinite(parsedSlope) && parsedSlope > 0 ? parsedSlope : NEUTRAL_SLOPE_RATING;

      const tees = [
        ...existingTees.map((tee) => ({
          name: tee.name,
          sex: tee.sex || 'unisex',
          teeColor: tee.name.toLowerCase(),
          courseRating: tee.courseRating,
          slopeRating: tee.slopeRating,
          par: tee.par,
        })),
        {
          name,
          sex: 'unisex',
          teeColor: name.toLowerCase(),
          courseRating,
          slopeRating,
          par: coursePar,
        },
      ];

      const courseName = (course.name ?? course.Name ?? selectedCourse?.name ?? '').trim();
      // The club name comes from the course's address, but geodata detail can return it blank. The
      // BFF rejects an empty club name (which silently failed the whole tee upsert), so fall back to
      // the course name to keep the upsert valid. The existing course keeps its club via courseId.
      const clubName =
        (course.address ?? course.Address ?? selectedCourse?.address ?? '').trim() || courseName;
      const visibility = (course.visibility ?? course.Visibility ?? 'private').toLowerCase();

      const requestId = generateId();
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);

      // The golfCourse.changed broadcast can be missed (e.g. after a WS reconnect), which made a
      // successful add look like a timeout. So don't rely on it alone: send the upsert, then confirm
      // by polling the course detail until the new tee appears (the same read that shows it after the
      // modal closes). A delivered rejection still fails fast with its reason.
      let rejectionReason: string | null = null;
      const unsubscribe = socket.onMessage((message) => {
        const rejected = message as GolfCourseUpsertRejectedEvent;
        const incomingRequestId =
          (rejected as { requestId?: string }).requestId ?? (rejected as { RequestId?: string }).RequestId;
        if (incomingRequestId === requestId && rejected.type === 'golfCourse.upsert.rejected') {
          rejectionReason = rejected.reason || 'rejected';
        }
      });

      try {
        socket.send({
          type: 'golfCourse.upsert',
          requestId,
          courseId: selectedCourseId,
          source: 'user',
          userId: user.id,
          clubName,
          courseName,
          holes,
          tees,
          visibility,
        });

        const deadline = Date.now() + 15000;
        let created: CourseTee | null = null;
        while (Date.now() < deadline && rejectionReason === null && created === null) {
          await new Promise<void>((resolveDelay) => setTimeout(() => resolveDelay(), 1500));
          try {
            const refreshed = await fetch(`${apiBaseUrl}/api/geodata/courses/${selectedCourseId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            });
            if (refreshed.ok) {
              const refreshedTees = mapCourseDetailTees((await refreshed.json()) as GeoCourseDetailResponse);
              const match = refreshedTees.find((tee) => tee.name.toLowerCase() === name.toLowerCase());
              if (match) {
                setCourseTees(refreshedTees);
                setSelectedTeeId(match.id);
                created = match;
              }
            }
          } catch {
            // Transient fetch error — keep polling until the deadline.
          }
        }

        if (rejectionReason !== null) {
          throw new Error(rejectionReason);
        }
        if (created === null) {
          throw new Error('add tee timeout');
        }
      } finally {
        unsubscribe();
      }

      logToBff('players.tee.add.success', { courseId: selectedCourseId, name });
      setAddTeeOpen(false);
      setAddTeeName('');
      setAddTeeCourseRating('');
      setAddTeeSlopeRating('');
    } catch (err) {
      // Surface the underlying reason (server rejection code or "add tee timeout") so a failure is
      // self-diagnosing instead of a generic message that hides whether it was rejected or timed out.
      const reason = err instanceof Error ? err.message : String(err);
      setAddTeeError(`${t('players.addTeeFailed')} (${reason})`);
      logToBff(
        'players.tee.add.failed',
        { courseId: selectedCourseId, message: reason },
        'warn'
      );
    } finally {
      setAddTeeSaving(false);
    }
  }, [
    addTeeCourseRating,
    addTeeName,
    addTeeSlopeRating,
    apiBaseUrl,
    logToBff,
    selectedCourse?.address,
    selectedCourse?.name,
    selectedCourseId,
    t,
    user?.id,
    user?.token,
  ]);

  useEffect(() => {
    if (!pendingRoundStartAfterDraftSave) return;
    if (draftRows.length > 0) return;
    if (pendingTemporaryPlayers > 0) return;
    setPendingRoundStartAfterDraftSave(false);
    startRoundInitiation({ navigateOnSuccess: true, suppressErrors: false, background: false });
  }, [draftRows.length, pendingRoundStartAfterDraftSave, pendingTemporaryPlayers, startRoundInitiation]);

  const hasMissingPlayingHandicap = useMemo(() => {
    const namedPlayers = players.filter((player) => player.name.trim().length > 0);
    if (namedPlayers.length === 0) return true;
    if (namedPlayers.some((player) => !player.scp.trim())) return true;
    // A name typed in the quick-add row requires a playing handicap before continuing.
    if (quickAddPlayerName.trim().length > 0 && !quickAddScp.trim()) return true;
    return draftRows.some((row) => {
      const hasManualDraftName = row.query.trim().length > 0;
      const requiresScp = hasManualDraftName && !row.selectedPlayerId && !row.temporarySelected;
      return requiresScp && !(row.scp ?? '').trim();
    });
  }, [draftRows, players, quickAddPlayerName, quickAddScp]);

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      {debug ? (
        <Text style={styles.headlineDebug}>
          {`userId=${user?.id ?? ''} playerId=${currentUserPlayerId ?? ''}`}
        </Text>
      ) : null}
      <View style={styles.courseCard}>
        <Text style={styles.courseLabel}>{t('players.courseLabel')}</Text>
        {visibleCourseError ? <Text style={styles.error}>{visibleCourseError}</Text> : null}
        {loadingCourses ? <ActivityIndicator /> : null}
        <TextInput
          value={courseSearchQuery}
          onChangeText={(value) => {
            setCourseSearchQuery(value);
            setCoursePickerOpen(true);
          }}
          onFocus={() => {
            if (!courseSearchQuery && selectedCourse) {
              setCourseSearchQuery(selectedCourse.name);
            }
            setCoursePickerOpen(true);
          }}
          onSubmitEditing={commitCourseFromQuery}
          placeholder={t('players.courseSearchPlaceholder')}
          placeholderTextColor="#8FA08B"
          style={styles.courseSearchField}
          editable={courses.length > 0}
        />
      </View>
      <View style={styles.setupRow}>
        <View style={[styles.setupCard, styles.setupCardHalf]}>
          <Text style={styles.setupCardLabel}>{t('players.holesCountLabel')}</Text>
          <View style={styles.holesCountOptions}>
            {[9, 18].map((count) => (
              <Pressable
                key={count}
                style={[styles.holesCountOption, holesCount === count && styles.holesCountOptionActive]}
                onPress={() => setHolesCount(count)}
                accessibilityLabel={t('players.holesCountOptionLabel', { count })}
              >
                <Text style={[styles.holesCountText, holesCount === count && styles.holesCountTextActive]}>
                  {t('players.holesCountOptionValue', { count })}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
        <View style={[styles.setupCard, styles.setupCardHalf]}>
          <View style={styles.teeTimeHeader}>
            <Text style={styles.setupCardLabel}>{t('players.playDateLabel')}</Text>
            <Pressable
              style={styles.roundDescriptionButton}
              onPress={() => setRoundDescriptionOpen(true)}
              accessibilityLabel={t('players.roundDescriptionButton')}
            >
              <Text style={styles.roundDescriptionButtonText}>⋯</Text>
            </Pressable>
          </View>
          <Pressable style={styles.playDateButton} onPress={() => setPlayDatePickerOpen(true)}>
            <Text style={styles.playDateButtonText}>{roundPlayDate}</Text>
          </Pressable>
        </View>
      </View>
      {selectedCourseId ? (
        <View style={styles.setupCard}>
          <View style={styles.teeCardHeader}>
            <Text style={styles.setupCardLabel}>{t('players.teeLabel')}</Text>
            <Pressable style={styles.addTeeButton} onPress={openAddTee} accessibilityLabel={t('players.addTee')}>
              <Text style={styles.addTeeButtonText}>＋ {t('players.addTee')}</Text>
            </Pressable>
          </View>
          {courseTees.length > 0 ? (
            <View style={styles.holesCountOptions}>
              <Pressable
                style={[styles.holesCountOption, selectedTeeId === null && styles.holesCountOptionActive]}
                onPress={() => setSelectedTeeId(null)}
                accessibilityLabel={t('players.teeNone')}
              >
                <Text style={[styles.holesCountText, selectedTeeId === null && styles.holesCountTextActive]}>
                  {t('players.teeNone')}
                </Text>
              </Pressable>
              {courseTees.map((tee) => {
                const isSelected = selectedTeeId === tee.id;
                return (
                  <Pressable
                    key={tee.id}
                    style={[styles.holesCountOption, isSelected && styles.holesCountOptionActive]}
                    onPress={() => setSelectedTeeId(tee.id)}
                    accessibilityLabel={tee.name}
                  >
                    <Text style={[styles.holesCountText, isSelected && styles.holesCountTextActive]}>
                      {tee.name}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
        </View>
      ) : null}
      <PlatformDatePicker
        open={playDatePickerOpen}
        date={parseDateInput(roundPlayDate) ?? new Date()}
        onConfirm={(date) => {
          setRoundPlayDate(formatDateInput(date));
          setPlayDatePickerOpen(false);
        }}
        onCancel={() => setPlayDatePickerOpen(false)}
        closeLabel={t('menu.close')}
      />
      <View style={styles.playersCard}>
        <View style={styles.playersHeaderRow}>
          <Text style={styles.playersHeaderText}>{t('summary.playerHeader')}</Text>
        </View>
        <View style={styles.playerHeadlineRow}>
          <Text style={[styles.playerHeadlineText, styles.playerHeadlinePlayer]}>{t('summary.playerHeader')}</Text>
          <Text numberOfLines={1} style={[styles.playerHeadlineText, styles.playerHeadlineHcp]}>
            {t('roundStartSetup.playingHandicapLabel')}
          </Text>
          <View style={styles.playerHeadlineActionSpacer} />
        </View>
        <PlayerMembershipEditor
          rows={membershipRows}
          availableMembers={availableMembersForEditor}
          rowOpen={playerRowOpen}
          saving={savingPlayers}
          maxPlayers={MAX_PLAYERS}
          disableAddRow={!canAddPlayerRow || savingPlayers}
          theme="blue"
          compact
          removeBeforeInline
          showConfirmButton={false}
          showAddButton={false}
          showTemporaryToggle={false}
          removeLabelMode="icon"
          labels={{
            addPlayer: t('tourEdit.addPlayer'),
            removePlayer: t('tourEdit.removePlayer'),
            searchPlaceholder: t('tourEdit.playerSearchPlaceholder'),
            noResults: t('tourEdit.playerSearchNoResults'),
            confirmA11y: t('tourEdit.playerConfirmA11y'),
            cancelA11y: t('tourEdit.playerCancelA11y'),
            temporaryToggleA11y: t('tourEdit.playerTemporaryToggleA11y'),
            temporarySelected: t('tourEdit.playerTemporarySelected'),
            createTemporary: t('tourEdit.playerCreateTemporary'),
          }}
          onAddRow={addPlayerRow}
          onRemoveRow={removeMembershipRow}
          onConfirmRow={confirmDraftRow}
          onToggleTemporary={onToggleTemporary}
          onDraftQueryChange={onDraftQueryChange}
          onDraftSelectOption={onDraftSelectOption}
          onDraftOpenChange={setPlayerRowOpen}
          renderDraftInline={(row) => {
            return (
              <TextInput
                value={row.scp ?? ''}
                onChangeText={(value) => onDraftScpChange(row.rowId, value)}
                placeholder={t('roundStartSetup.playingHandicapPlaceholder')}
                placeholderTextColor="#8FA08B"
                keyboardType="numeric"
                style={styles.scpInput}
              />
            );
          }}
          renderExistingInline={(row) => {
            const index = players.findIndex((player) => player.id === row.playerId);
            if (index < 0 || !row.name) return null;
            return (
              <TextInput
                value={players[index]?.scp ?? ''}
                onChangeText={(value) => onPlayerScpChange(index, value)}
                placeholder={t('roundStartSetup.playingHandicapPlaceholder')}
                placeholderTextColor="#8FA08B"
                keyboardType="numeric"
                style={styles.scpInput}
              />
            );
          }}
          renderExistingTrailing={(row) => {
            if (!debug) return null;
            return (
              <View style={styles.roundInitPlayerMetaRow}>
                <Text style={[styles.playerDebugLabel, row.locked && styles.playerDebugLabelCurrent]}>
                  {row.locked ? `userId=${user?.id ?? ''} playerId=${row.playerId}` : `playerId=${row.playerId}`}
                </Text>
              </View>
            );
          }}
        />
        <View style={styles.quickAddRow}>
          <TextInput
            value={quickAddPlayerName}
            onChangeText={setQuickAddPlayerName}
            onSubmitEditing={addPlayerByName}
            placeholder={`${t('tourEdit.addPlayer')}...`}
            placeholderTextColor="#8FA08B"
            style={styles.quickAddInput}
            editable={!savingPlayers && canAddPlayerRow}
          />
          <TextInput
            value={quickAddScp}
            onChangeText={setQuickAddScp}
            onSubmitEditing={addPlayerByName}
            placeholder={t('roundStartSetup.playingHandicapPlaceholder')}
            placeholderTextColor="#8FA08B"
            keyboardType="numeric"
            style={styles.scpInput}
            editable={!savingPlayers && canAddPlayerRow}
          />
          <View style={styles.playerHeadlineActionSpacer} />
        </View>
        {quickAddSuggestions.length > 0 ? (
          <View style={styles.quickAddSuggestions}>
            {quickAddSuggestions.map((member) => (
              <Pressable
                key={member.id}
                style={styles.quickAddSuggestionItem}
                onPress={() => addKnownPlayer(member.id, member.name, member.userId ?? null, quickAddScp)}
                accessibilityLabel={`${t('tourEdit.addPlayer')}: ${member.name}`}
              >
                <Text style={styles.quickAddSuggestionText}>{member.name}</Text>
              </Pressable>
            ))}
          </View>
        ) : null}
        {debug && debugPendingTemporaryCreates.length > 0 ? (
          <View style={styles.roundInitPlayerMetaRow}>
            {debugPendingTemporaryCreates.map((pending) => (
              <Text key={pending.requestId} style={styles.playerDebugLabel}>
                {`pendingTemp requestId=${pending.requestId} name=${pending.name} rowId=${pending.rowId || '-'}`}
              </Text>
            ))}
          </View>
        ) : null}
        {visiblePlayerError ? <Text style={styles.error}>{visiblePlayerError}</Text> : null}
      </View>
      {showRoundShare ? (
        <GoffyButton
          label={t('tournaments.shareAction')}
          onPress={() => void handleOpenRoundShare()}
          disabled={shareModalLoading}
          tone="dark"
          size="compact"
          style={styles.shareRoundButton}
        />
      ) : null}
      {visibleCourseError ? <Text style={styles.continueError}>{visibleCourseError}</Text> : null}
      <GoffyButton
        label={continueLabel}
        onPress={handleSelectGames}
        disabled={savingPlayers || pendingTemporaryPlayers > 0 || hasMissingPlayingHandicap}
        tone="accent"
        size="compact"
        style={styles.selectGamesButton}
      />
      <Modal visible={coursePickerOpen} transparent animationType="fade" onRequestClose={() => setCoursePickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setCoursePickerOpen(false)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <TextInput
              value={courseSearchQuery}
              onChangeText={setCourseSearchQuery}
              onSubmitEditing={commitCourseFromQuery}
              placeholder={t('players.courseSearchPlaceholder')}
              placeholderTextColor="#2d6b83"
              style={styles.courseSearchInput}
              autoCorrect={false}
              autoCapitalize="none"
              autoFocus
            />
            <Text style={styles.courseSearchHint}>{t('players.searchAnyCourseHint')}</Text>
            <FlatList
              data={filteredCourses}
              keyExtractor={(item) => item.id}
              ListHeaderComponent={
                showRecentCourses ? (
                  <View>
                    <Text style={styles.recentSectionTitle}>
                      {t('players.recentlyPlayedTitle')}
                      <Text style={styles.recentSectionSubtitle}> · {t('players.recentlyPlayedSubtitle')}</Text>
                    </Text>
                    {recentCourses.map((course: RecentCourse) => (
                      <Pressable
                        key={course.lastRoundId || course.id}
                        style={[styles.recentRow, course.id === selectedCourseId && styles.courseOptionActive]}
                        onPress={() => selectCourse(course.id, course.name)}>
                        <View style={styles.recentRowText}>
                          <Text style={styles.courseOptionTitle}>{course.name}</Text>
                          {course.address ? <Text style={styles.courseOptionSubtitle}>{course.address}</Text> : null}
                        </View>
                        {formatLastPlayed(course) ? (
                          <Text style={styles.recentRowMeta}>{formatLastPlayed(course)}</Text>
                        ) : null}
                      </Pressable>
                    ))}
                    <View style={styles.pickerDividerRow}>
                      <View style={styles.pickerDividerLine} />
                      <Text style={styles.pickerDividerText}>{t('players.lookingForAnother')}</Text>
                      <View style={styles.pickerDividerLine} />
                    </View>
                    {filteredCourses.length > 0 ? (
                      <Text style={styles.nearbyLabel}>{t('players.nearbyCoursesTitle')}</Text>
                    ) : null}
                  </View>
                ) : null
              }
              renderItem={({ item }) => (
                <Pressable
                  style={[
                    styles.courseOption,
                    item.id === selectedCourseId && styles.courseOptionActive,
                  ]}
                  onPress={() => selectCourse(item.id, item.name)}>
                  <Text style={styles.courseOptionTitle}>{item.name}</Text>
                  <Text style={styles.courseOptionSubtitle}>{item.address}</Text>
                  <Text
                    style={[
                      styles.courseBadge,
                      item.visibility === 'private'
                        ? styles.courseBadgePrivate
                        : item.visibility === 'pending'
                          ? styles.courseBadgePending
                          : styles.courseBadgeOfficial,
                    ]}>
                    {item.visibility === 'private'
                      ? t('players.coursePrivateBadge')
                      : item.visibility === 'pending'
                        ? t('players.coursePendingBadge')
                        : t('players.courseOfficialBadge')}
                  </Text>
                </Pressable>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              ListEmptyComponent={showRecentCourses ? null : <Text style={styles.emptyState}>{t('players.courseSearchEmpty')}</Text>}
            />
            <Pressable style={styles.addCourseButton} onPress={handleAddCourse} accessibilityLabel={t('players.addCourse')}>
              <Text style={styles.addCourseButtonText}>{t('players.addCourse')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={roundDescriptionOpen} transparent animationType="fade" onRequestClose={() => setRoundDescriptionOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundDescriptionOpen(false)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('players.roundDescriptionTitle')}</Text>
            <TextInput
              value={roundDescription}
              onChangeText={setRoundDescription}
              placeholder={t('players.roundDescriptionPlaceholder')}
              placeholderTextColor="#8FA08B"
              multiline
              style={styles.roundDescriptionInput}
            />
            <Pressable style={styles.modalCloseButton} onPress={() => setRoundDescriptionOpen(false)}>
              <Text style={styles.modalCloseText}>{t('menu.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal visible={addTeeOpen} transparent animationType="fade" onRequestClose={() => setAddTeeOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => (addTeeSaving ? null : setAddTeeOpen(false))}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('players.addTeeTitle')}</Text>
            <Text style={styles.addTeeFieldLabel}>{t('players.addTeeNameLabel')}</Text>
            <TextInput
              value={addTeeName}
              onChangeText={(value) => {
                setAddTeeName(value);
                if (addTeeError) setAddTeeError(null);
              }}
              placeholder={t('players.addTeeNamePlaceholder')}
              placeholderTextColor="#8FA08B"
              autoFocus
              style={styles.addTeeInput}
            />
            <Text style={styles.addTeeFieldLabel}>{t('players.addTeeCourseRatingLabel')}</Text>
            <TextInput
              value={addTeeCourseRating}
              onChangeText={setAddTeeCourseRating}
              keyboardType="decimal-pad"
              style={styles.addTeeInput}
            />
            <Text style={styles.addTeeFieldLabel}>{t('players.addTeeSlopeRatingLabel')}</Text>
            <TextInput
              value={addTeeSlopeRating}
              onChangeText={setAddTeeSlopeRating}
              keyboardType="number-pad"
              style={styles.addTeeInput}
            />
            {addTeeError ? <Text style={styles.error}>{addTeeError}</Text> : null}
            <View style={styles.addTeeActions}>
              <Pressable
                style={styles.modalCloseButton}
                onPress={() => setAddTeeOpen(false)}
                disabled={addTeeSaving}
              >
                <Text style={styles.modalCloseText}>{t('menu.close')}</Text>
              </Pressable>
              <GoffyButton
                label={addTeeSaving ? t('home.saving') : t('players.addTeeSave')}
                onPress={() => void handleCreateTee()}
                disabled={addTeeSaving || !addTeeName.trim()}
                tone="accent"
                size="compact"
                style={styles.addTeeSaveButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setShareModalOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('players.inviteOthers')}</Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{shareJoinCode || '--- ---'}</Text>
              {shareModalLoading ? (
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              ) : null}
              {shareQrVisible && shareModalLink ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={shareModalLink} size={220} color="#000000" backgroundColor="#ffffff" />
                </View>
              ) : null}
            </View>
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('players.showQr')}
                onPress={() => setShareQrVisible((prev) => !prev)}
                disabled={!shareModalLink || shareModalLoading}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareRoundInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#011806',
  },
  container: {
    flexGrow: 1,
    padding: 24,
    gap: 16,
    backgroundColor: '#011806',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
  },
  headlineDebug: {
    textAlign: 'center',
    color: '#ef4444',
    fontSize: 12,
    fontWeight: '600',
  },
  subtitle: {
    color: '#A1D493',
    fontSize: 13,
  },
  roundsCard: {
    borderRadius: 16,
    padding: 16,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 10,
    overflow: 'visible',
    zIndex: 10,
    elevation: 2,
  },
  roundsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundsTitle: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  finishedRoundsBlock: {
    gap: 8,
    paddingVertical: 6,
  },
  finishedRoundsTitle: {
    color: '#ffd972',
    fontWeight: '700',
  },
  roundRow: {
    width: '100%',
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    position: 'relative',
    overflow: 'visible',
    zIndex: 1,
  },
  roundRowMenuOpen: {
    zIndex: 30,
  },
  roundRowFinished: {
    backgroundColor: 'rgba(255, 217, 114, 0.12)',
    borderColor: 'rgba(255, 217, 114, 0.4)',
  },
  roundContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roundMenuButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundMenuIcon: {
    color: appColors.text.primary,
    fontSize: 18,
    lineHeight: 18,
    fontWeight: '700',
  },
  roundMenu: {
    position: 'absolute',
    top: 38,
    left: 8,
    backgroundColor: '#07200D',
    borderRadius: 12,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 6,
    zIndex: 50,
    elevation: 8,
  },
  roundMenuItem: {
    paddingVertical: 6,
  },
  roundMenuText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  roundName: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  roundMeta: {
    color: '#A1D493',
    fontSize: 12,
  },
  roundOpen: {
    color: '#A1D493',
    fontWeight: '700',
  },
  roundResults: {
    color: '#ffd972',
    fontWeight: '700',
  },
  courseCard: {
    borderRadius: 16,
    padding: 18,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    zIndex: 0,
    gap: 10,
  },
  courseLabel: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
  },
  error: {
    color: '#ff6666',
    marginBottom: 8,
  },
  courseSelect: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    padding: 12,
    backgroundColor: '#07200D',
  },
  courseSelectDisabled: {
    opacity: 0.7,
  },
  courseSelectText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  courseSearchInput: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    paddingVertical: 8,
    paddingHorizontal: 10,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
    marginBottom: 8,
  },
  courseSearchField: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '600',
  },
  setupRow: {
    flexDirection: 'row',
    gap: 12,
  },
  setupCard: {
    borderRadius: 16,
    padding: 16,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 10,
  },
  setupCardHalf: {
    flex: 1,
  },
  setupCardLabel: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
  },
  teeTimeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roundMetaRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  playDateRow: {
    marginTop: 10,
    gap: 6,
  },
  playDateLabel: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  playDateButton: {
    alignSelf: 'stretch',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#07200D',
  },
  playDateButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  holesCountRow: {
    marginTop: 10,
    gap: 10,
  },
  holesCountLabel: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  holesCountOptions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  holesCountOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0B2A14',
  },
  holesCountOptionActive: {
    borderColor: '#8BCF8A',
    backgroundColor: '#8BCF8A',
  },
  holesCountText: {
    color: '#BFD0BC',
    fontWeight: '600',
  },
  holesCountTextActive: {
    color: '#07200D',
  },
  roundDescriptionButton: {
    width: 28,
    height: 28,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundDescriptionButtonText: {
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 18,
  },
  roundNameInput: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 8,
    paddingHorizontal: 12,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
  },
  playerList: {
    gap: 12,
  },
  playersCard: {
    borderRadius: 16,
    padding: 16,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 10,
  },
  playersHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  playersHeaderText: {
    color: appColors.text.primary,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontSize: 16,
  },
  quickAddRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  quickAddSuggestions: {
    gap: 8,
  },
  quickAddSuggestionItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0B2A14',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  quickAddSuggestionText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '600',
  },
  quickAddInput: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
    fontSize: 16,
  },
  playerHeadlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
    gap: 8,
  },
  playerHeadlineText: {
    color: '#b8c8ee',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  playerHeadlinePlayer: {
    flex: 1,
  },
  playerHeadlineHcp: {
    width: 96,
  },
  playerHeadlineActionSpacer: {
    width: 58,
  },
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  playerLabel: {
    width: 70,
    color: appColors.text.primary,
    fontWeight: '600',
  },
  playerLabelRow: {
    width: 90,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  playerDebugLabel: {
    color: '#8FA08B',
    fontSize: 10,
  },
  playerDebugLabelCurrent: {
    color: '#ef4444',
  },
  roundInitPlayerMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  input: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    color: appColors.text.primary,
  },
  playerSelect: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
  },
  playerSelectDisabled: {
    borderColor: '#1A3A20',
    backgroundColor: appColors.surface.alt,
  },
  playerSelectLocked: {
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  playerSelectText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  scpInput: {
    width: 78,
    height: 38,
    paddingLeft: 8,
    paddingRight: 8,
    paddingVertical: 0,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    color: appColors.text.primary,
  },
  removeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#ff6666',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonSpacer: {
    width: 36,
    height: 36,
  },
  removeButtonDisabled: {
    backgroundColor: '#1A3A20',
  },
  removeButtonText: {
    color: appColors.text.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  addButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: '#A1D493',
    borderWidth: 1,
    borderColor: '#1aa374',
  },
  addButtonDisabled: {
    backgroundColor: '#1A3A20',
    borderColor: '#1A3A20',
  },
  addButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  addButtonImageMode: {
    marginTop: 4,
    width: 220,
    height: 56,
  },
  addButtonImageText: {
    fontSize: 16,
  },
  addTemporaryButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: '#1A3A20',
    borderWidth: 1,
    borderColor: '#A1D493',
  },
  addTemporaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  selectGamesButton: {
    marginTop: 12,
    alignSelf: 'center',
  },
  shareRoundButton: {
    marginTop: 4,
    alignSelf: 'center',
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: '#d5f3cc',
    fontSize: 42,
    fontWeight: '500',
    fontStyle: 'italic',
    fontFamily: 'serif',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 52,
    letterSpacing: 8,
    fontWeight: '700',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  shareActions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center',
  },
  shareActionButton: {
    flex: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#07200D',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 12,
    maxHeight: '70%',
  },
  modalTitle: {
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  modalCloseButton: {
    marginTop: 12,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#1A3A20',
  },
  modalCloseText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  teeCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  addTeeButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#1A3A20',
    borderWidth: 1,
    borderColor: '#A1D493',
  },
  addTeeButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  addTeeFieldLabel: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 4,
  },
  addTeeInput: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
  },
  addTeeActions: {
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  addTeeSaveButton: {
    flex: 1,
  },
  temporaryActionsRow: {
    marginTop: 8,
    flexDirection: 'row',
    gap: 8,
  },
  temporaryNameInput: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
  },
  temporaryCreateButton: {
    minWidth: 130,
    alignSelf: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#A1D493',
  },
  temporaryCreateButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  roundDescriptionInput: {
    minHeight: 120,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: appColors.text.primary,
    backgroundColor: '#07200D',
    textAlignVertical: 'top',
  },
  courseOption: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  courseOptionActive: {
    backgroundColor: '#1A3A20',
  },
  courseSearchHint: {
    color: '#7FA978',
    fontSize: 12,
    marginBottom: 10,
    paddingHorizontal: 2,
  },
  recentSectionTitle: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
    marginBottom: 4,
  },
  recentSectionSubtitle: {
    color: '#5f7e5a',
    fontWeight: '400',
    textTransform: 'none',
    letterSpacing: 0,
  },
  recentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  recentRowText: {
    flex: 1,
    minWidth: 0,
  },
  recentRowMeta: {
    color: '#7FA978',
    fontSize: 11,
    textAlign: 'right',
  },
  pickerDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 8,
    marginVertical: 10,
    paddingHorizontal: 2,
  },
  pickerDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A3A20',
  },
  pickerDividerText: {
    color: '#5f7e5a',
    fontSize: 12,
  },
  nearbyLabel: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
    marginBottom: 4,
  },
  courseOptionTitle: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  courseOptionSubtitle: {
    color: '#A1D493',
    fontSize: 12,
  },
  courseBadge: {
    marginTop: 4,
    alignSelf: 'flex-start',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  courseBadgeOfficial: {
    color: '#06280F',
    backgroundColor: '#A1D493',
  },
  courseBadgePrivate: {
    color: '#FCD34D',
    backgroundColor: 'rgba(252,211,77,0.15)',
  },
  courseBadgePending: {
    color: '#93C5FD',
    backgroundColor: 'rgba(147,197,253,0.15)',
  },
  separator: {
    height: 1,
    backgroundColor: '#1A3A20',
  },
  emptyState: {
    paddingVertical: 12,
    textAlign: 'center',
    color: '#C8C8B0',
  },
  addCourseButton: {
    marginTop: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0B2A14',
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  addCourseButtonText: {
    color: '#A1D493',
    fontWeight: '700',
  },
  continueError: {
    color: '#ff6666',
    fontWeight: '700',
    marginTop: 4,
    marginBottom: 2,
  },
});

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
